import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { TeamSorterView, ScorecardView, PlayoffsView, FinalStandingsView } from "./AthleticsDashboard";
import { NavigatorIntramuralsLanding } from "./NavigatorIntramurals";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import HobbiesPage from "./HobbiesPage";
import AssistantHobbiesView from "../components/AssistantHobbiesView";
import HeadPasswordResetModal from "../components/HeadPasswordResetModal";
import MenuSearchBar from "../components/MenuSearchBar";

function decodeJwtPayload(token) {
  try {
    const part = (token || "").split(".")[1] || "";
    const padded = part + "=".repeat((4 - (part.length % 4)) % 4);
    const json = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch { return null; }
}

function formatPhone(raw) {
  if (!raw) return "";
  const d = raw.replace(/\D/g, "");
  if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  if (d.length === 11 && d[0] === "1") return `(${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`;
  return raw;
}
import { ScrollArea } from "../components/ui/scroll-area";
import { 
  Search, LogOut, User, Phone, Mail, AlertTriangle, AlertCircle,
  Stethoscope, Calendar, RefreshCw, Users, Download, FileText,
  Heart, Brain, Star, CalendarDays, Bus, Waves, ClipboardList, MapPin, Plus, Minus, Printer, Droplets, MessageSquare, ArrowLeft, Utensils, Save, X, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Sparkles, Bell, CheckCircle2, Circle, XCircle, Flag, Trophy, Swords, Clock, ExternalLink, Moon, Drama, Pizza, Crown
} from "lucide-react";
import BuddyCheckSheet from "./BuddyCheckSheet";
import MessagesPage from "./MessagesPage";
import TripRostersView from "../components/TripRostersView";
import CamperPhoto from "../components/CamperPhoto";
import IncidentReportPage from "./IncidentReportPage";
import TripAccountabilityForm from "./TripAccountabilityForm";
import TeamsView from "../components/TeamsView";
import ScheduleViewer from "./ScheduleViewer";
import ScheduleNotificationsBanner from "./ScheduleNotificationsBanner";
import CarnivalSnackOrder from "./CarnivalSnackOrder";
import { useCounselorHeartbeat } from "../hooks/useCounselorHeartbeat";
import ReorderableGrid from "../components/ReorderableGrid";
import confetti from "canvas-confetti";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/assets/rrdc-logo.png`;
const HOBBY_ELIGIBLE = new Set(["B08","B09","B10","B11","G08","G09","G10","G11","B12","B13","B14","B15","G12","G13","G14","G15"]);
const NAV_GROUPS = new Set(["B12","B13","B14","B15","G12","G13","G14","G15"]);
const TEAMS_GROUPS = new Set(["B05","B06","B07","B08","B09","B10","B11","B12","B13","B14","B15","G05","G06","G07","G08","G09","G10","G11","G12","G13","G14","G15","CIT"]);
const INTRAMURAL_BOYS = new Set(["B08","B09","B10","B11"]);
const INTRAMURAL_GIRLS = new Set(["G08","G09","G10","G11"]);

// Navigator/CIT optional trip dates (keep in sync with backend constants)
const BROADWAY_TRIP_DATE = "2026-07-22";
const OVERNIGHT_TRIP_DATES = ["2026-07-28", "2026-07-29", "2026-07-30"];

/* Today's Birthdays banner — persistent at top of counselor menu only on the
   actual day a camper in this group has their birthday. Pulls DOBs from the
   already-loaded `campers` array (no extra API call).  */
function TodaysBirthdaysBanner({ campers }) {
  const todays = (() => {
    if (!Array.isArray(campers) || campers.length === 0) return [];
    // Today in America/New_York as MM-DD
    const now = new Date();
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York", month: "2-digit", day: "2-digit",
    }).formatToParts(now);
    const month = fmt.find(p => p.type === "month")?.value || "";
    const day = fmt.find(p => p.type === "day")?.value || "";
    if (!month || !day) return [];
    const todayKey = `${month}-${day}`;
    return campers.filter(c => {
      const dob = c.date_of_birth || "";
      if (!dob || dob.length < 10) return false;
      // dob format: YYYY-MM-DD
      return dob.slice(5, 10) === todayKey;
    });
  })();

  if (todays.length === 0) return null;

  return (
    <div
      className="mb-3 p-3 rounded-xl border-2 border-pink-400 bg-gradient-to-r from-pink-50 via-purple-50 to-amber-50 shadow"
      data-testid="todays-birthdays-banner"
    >
      <div className="flex items-start gap-2">
        <span className="text-2xl leading-none select-none">🎂</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-pink-700">
            Happy Birthday today!
          </p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {todays.map(c => {
              const display = c.preferred_name?.trim() || c.first_name || "";
              return (
                <span
                  key={c.id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-pink-300 text-xs font-semibold text-pink-700 shadow-sm"
                  data-testid={`birthday-${c.id}`}
                >
                  {display} {c.last_name || ""}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* Persistent banner — shows the counselor's group changing area(s) and
   lunch table(s) at the top of every dashboard view. Read-only. */
function LocationPill({ label, count, showCount, tone }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
        tone === "teal"
          ? "bg-teal-50 border-teal-300 text-teal-800"
          : "bg-amber-50 border-amber-300 text-amber-800"
      }`}
    >
      {label}
      {showCount && (
        <span className={`px-1 rounded text-[10px] ${tone === "teal" ? "bg-teal-200 text-teal-900" : "bg-amber-200 text-amber-900"}`}>
          ×{count}
        </span>
      )}
    </span>
  );
}

function ColorWarScoreBanner() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("token");
    axios
      .get(`${API}/color-war/scores`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!data?.winner || data.hidden) return;
    const key = `cw-confetti-${data.winner}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
    const colors = data.winner === "blue"
      ? ["#2563eb", "#60a5fa", "#facc15", "#ffffff"]
      : ["#facc15", "#fde047", "#ffffff", "#cbd5e1"];
    confetti({ particleCount: 180, spread: 100, origin: { y: 0.6 }, colors });
    const end = Date.now() + 3500;
    const frame = () => {
      confetti({ particleCount: 6, angle: 60, spread: 60, origin: { x: 0, y: 0.7 }, colors });
      confetti({ particleCount: 6, angle: 120, spread: 60, origin: { x: 1, y: 0.7 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();
  }, [data]);

  if (!data || data.hidden || (!data.blue_score && !data.white_score && !data.winner)) return null;

  const winner = data.winner;  const goldSide = "bg-gradient-to-br from-amber-300 via-yellow-400 to-amber-500 text-amber-950";
  const winnerName = winner === "blue" ? data.blue_name : data.white_name;

  return (
    <div className={`mb-3 rounded-xl overflow-hidden border-2 shadow-sm ${winner ? "border-amber-400 shadow-amber-200/60 shadow-lg" : "border-blue-300"}`} data-testid="cw-score-banner">
      <div className={`flex items-center justify-center gap-2 py-1 ${winner ? "bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500" : "bg-gradient-to-r from-blue-600 to-blue-700"}`}>
        {winner ? <Crown className="w-4 h-4 text-white" /> : <Trophy className="w-3.5 h-3.5 text-yellow-300" />}
        <span className="text-[11px] font-bold uppercase tracking-widest text-white" data-testid="cw-score-banner-title">
          {winner ? `${winnerName} are the Color War Champions!` : "Color War Scores"}
        </span>
        {winner ? <Crown className="w-4 h-4 text-white" /> : <Trophy className="w-3.5 h-3.5 text-yellow-300" />}
      </div>
      <div className="flex">
        <div className={`flex-1 py-2.5 px-3 text-center ${winner === "blue" ? goldSide : "bg-blue-600 text-white"}`} data-testid="cw-score-banner-blue">
          <p className={`flex items-center justify-center gap-1 text-[11px] font-bold uppercase tracking-wide ${winner === "blue" ? "text-amber-900" : "text-blue-100"}`}>
            {winner === "blue" && <Crown className="w-3.5 h-3.5" />}{data.blue_name}{winner === "blue" && <Crown className="w-3.5 h-3.5" />}
          </p>
          <p className="text-2xl font-black leading-tight">{data.blue_score || "—"}</p>
        </div>
        <div className={`flex-1 py-2.5 px-3 text-center border-l-2 ${winner === "white" ? `${goldSide} border-amber-400` : "bg-white text-slate-800 border-blue-300"}`} data-testid="cw-score-banner-white">
          <p className={`flex items-center justify-center gap-1 text-[11px] font-bold uppercase tracking-wide ${winner === "white" ? "text-amber-900" : "text-slate-500"}`}>
            {winner === "white" && <Crown className="w-3.5 h-3.5" />}{data.white_name}{winner === "white" && <Crown className="w-3.5 h-3.5" />}
          </p>
          <p className="text-2xl font-black leading-tight">{data.white_score || "—"}</p>
        </div>
      </div>
    </div>
  );
}

function GroupLocationsBanner({ groupCode }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!groupCode) return;
    let cancelled = false;
    setLoading(true);
    const token = localStorage.getItem("token");
    axios
      .get(`${API}/locations/group-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [groupCode]);

  if (loading || !data) return null;
  const changing = data.changing_areas || [];
  const tables = data.lunch_tables || [];
  const locations = data.lunch_locations || [];
  const lunchTime = (data.lunch_time || "").trim();
  const cabanaTime = (data.cabana_time || "").trim();
  if (
    !changing.length && !tables.length && !locations.length &&
    !lunchTime && !cabanaTime
  ) return null;

  return (
    <div
      className="mb-3 p-3 rounded-xl border-2 border-teal-300 bg-gradient-to-r from-teal-50 to-amber-50 shadow-sm"
      data-testid="group-locations-banner"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
        {!!changing.length && (
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-xs font-bold text-teal-800 mb-1">
              <MapPin className="w-3.5 h-3.5" />
              Changing Area{changing.length > 1 ? "s" : ""}
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="banner-changing-areas">
              {changing.map((c) => (
                <LocationPill
                  key={c.label}
                  label={c.label}
                  count={c.count}
                  showCount={changing.length > 1}
                  tone="teal"
                />
              ))}
            </div>
          </div>
        )}
        {(tables.length > 0 || locations.length > 0) && (
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-xs font-bold text-amber-800 mb-1">
              <Utensils className="w-3.5 h-3.5" />
              Lunch {tables.length > 0 ? "Table" : "Location"}{(tables.length + locations.length) > 1 ? "s" : ""}
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="banner-lunch-tables">
              {tables.map((t) => (
                <LocationPill
                  key={`t-${t.label}`}
                  label={t.label}
                  count={t.count}
                  showCount={tables.length + locations.length > 1}
                  tone="amber"
                />
              ))}
              {locations.map((l) => (
                <LocationPill
                  key={`l-${l.label}`}
                  label={l.label}
                  count={l.count}
                  showCount={tables.length + locations.length > 1}
                  tone="amber"
                />
              ))}
            </div>
          </div>
        )}
      </div>
      {data.missing_assignment_count > 0 && (
        <p className="mt-2 text-[11px] text-gray-600" data-testid="banner-missing-count">
          {data.missing_assignment_count} camper{data.missing_assignment_count === 1 ? "" : "s"} without a changing area or lunch table assignment.
        </p>
      )}
      {(lunchTime || cabanaTime) && (
        <div className="mt-2 pt-2 border-t border-teal-200/60 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {lunchTime && (
            <div className="flex items-start gap-1.5" data-testid="banner-lunch-time">
              <Clock className="w-3.5 h-3.5 text-amber-700 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-wide font-bold text-amber-800">Lunch Time</div>
                <div className="text-xs font-semibold text-gray-800">{lunchTime}</div>
              </div>
            </div>
          )}
          {cabanaTime && (
            <div className="flex items-start gap-1.5" data-testid="banner-cabana-time">
              <Waves className="w-3.5 h-3.5 text-sky-700 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wide font-bold text-sky-800">Cabana Time</div>
                <div className="text-xs font-semibold text-gray-800 break-words">{cabanaTime}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CounselorDashboard() {
  const { user, logout } = useAuth();
  const isAssistant = user?.user_type === "assistant" || !!user?.is_assistant;

  // Head-counselor forced password reset gate.
  // Token carries must_set_password=true the first time someone logs in
  // with the shared group-name password. Modal blocks the entire dashboard
  // until they pick a new one (or log out).
  const [mustSetPw, setMustSetPw] = useState(() => {
    const tk = localStorage.getItem("token");
    const p = decodeJwtPayload(tk);
    return !isAssistant && !!p?.must_set_password;
  });

  // 30-min idle auto-logout + 60s heartbeat (counselor only)
  useCounselorHeartbeat({
    enabled: user?.user_type === "counselor",
    onIdleLogout: () => {
      toast.warning("You've been logged out due to inactivity.");
      logout();
    },
  });

  const [campers, setCampers] = useState([]);
  const [filteredCampers, setFilteredCampers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCamper, setSelectedCamper] = useState(null);
  const [customFields, setCustomFields] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEvalForm, setShowEvalForm] = useState(false);
  const [evalSubmitted, setEvalSubmitted] = useState(false);
  const [evalSubmitting, setEvalSubmitting] = useState(false);
  const [showTripForm, setShowTripForm] = useState(false);
  const [showBuddySheet, setShowBuddySheet] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [showIncidentReport, setShowIncidentReport] = useState(false);
  const [showHobbies, setShowHobbies] = useState(false);
  const [showAssistantHobbies, setShowAssistantHobbies] = useState(false);
  const [showAssistantIntensives, setShowAssistantIntensives] = useState(false);
  const [showIntensives, setShowIntensives] = useState(false);
  const [showAttendance, setShowAttendance] = useState(false);
  const [attendanceData, setAttendanceData] = useState({});
  const [attendanceConflicts, setAttendanceConflicts] = useState({});
  const [attendanceSaving, setAttendanceSaving] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [showSchedule, setShowSchedule] = useState(false);
  const [hasSchedule, setHasSchedule] = useState(false);
  const [menuLunchTally, setMenuLunchTally] = useState(null);
  const [currentView, setCurrentView] = useState("menu");
  const [showCarnival, setShowCarnival] = useState(false);
  const [carnivalEnabled, setCarnivalEnabled] = useState(false);
  const [attendanceAlerts, setAttendanceAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const [alertsData, setAlertsData] = useState({ active: [], dismissed: [], active_count: 0 });
  const [rosterData, setRosterData] = useState(null);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [rosterSelectedDate, setRosterSelectedDate] = useState("");
  const [paperworkData, setPaperworkData] = useState(null);
  const [paperworkLoading, setPaperworkLoading] = useState(false);
  const [citTripsData, setCitTripsData] = useState(null);
  const [citTripsLoading, setCitTripsLoading] = useState(false);
  const [citChoicesData, setCitChoicesData] = useState(null);
  const [citChoicesLoading, setCitChoicesLoading] = useState(false);
  const [citChoicesSelected, setCitChoicesSelected] = useState(null);
  const [citChoicesSearch, setCitChoicesSearch] = useState("");
  const [citOvernightData, setCitOvernightData] = useState(null);
  const [citOvernightLoading, setCitOvernightLoading] = useState(false);
  const [pmSwimLessons, setPmSwimLessons] = useState({ pm_campers: [], am_campers: [], weekday: "", date: "" });
  const [amSwimDismissed, setAmSwimDismissed] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([new Date().getFullYear()]);
  const [evalForm, setEvalForm] = useState({
    head_counselor: "",
    activity_appropriateness: "",
    activity_frequency: "",
    programming_improvement: "",
    other_ideas: ""
  });

  const isCurrentYear = selectedYear === new Date().getFullYear();

  const fetchYears = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sessions/years`);
      setAvailableYears(res.data.years || [new Date().getFullYear()]);
    } catch { /* non-critical */ }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [campersRes, fieldsRes, syncRes] = await Promise.all([
        axios.get(`${API}/campers`, { params: { year: selectedYear } }),
        axios.get(`${API}/custom-fields`),
        axios.get(`${API}/sync/status`)
      ]);
      const sortByLastName = (list) => [...list].sort((a, b) =>
        (a.last_name || "").localeCompare(b.last_name || "") ||
        (a.first_name || "").localeCompare(b.first_name || "")
      );
      setCampers(sortByLastName(campersRes.data));
      setFilteredCampers(sortByLastName(campersRes.data));
      setCustomFields(fieldsRes.data);
      setSyncStatus(syncRes.data);
      
      // Check if evaluation already submitted
      try {
        const evalRes = await axios.get(`${API}/evaluations/${user?.group_code}`);
        if (evalRes.data) {
          setEvalSubmitted(true);
          setEvalForm({
            head_counselor: evalRes.data.head_counselor || "",
            activity_appropriateness: evalRes.data.responses?.activity_appropriateness || "",
            activity_frequency: evalRes.data.responses?.activity_frequency || "",
            programming_improvement: evalRes.data.responses?.programming_improvement || "",
            other_ideas: evalRes.data.responses?.other_ideas || ""
          });
        }
      } catch (e) { /* not submitted yet */ }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [user?.group_code, selectedYear]);

  useEffect(() => {
    fetchYears();
  }, [fetchYears]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Light fetch — only the on/off flag. The full roster lives inside the
  // carnival view itself.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const res = await axios.get(`${API}/carnival-snack/config`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setCarnivalEnabled(!!res.data?.enabled);
      } catch (_e) { /* silent — card just stays hidden */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Add showCarnival to the menu check below
  // ── Confetti banner: only on the dashboard MENU, not on any sub-view ──
  // Sub-views (BuddySheet, Messages, IncidentReport, Trip, Schedule, Hobbies,
  // Intensives, Attendance, Alerts) early-return BEFORE the menu JSX without
  // changing currentView, so we have to check every show-flag here.
  const isOnMenu = currentView === "menu"
    && !loading
    && !showBuddySheet
    && !showMessages
    && !showIncidentReport
    && !showEvalForm
    && !showTripForm
    && !showSchedule
    && !showHobbies
    && !showIntensives
    && !showAttendance
    && !showAlerts
    && !showCarnival;

  useEffect(() => {
    if (!isOnMenu) {
      const el = document.getElementById("rrdc-confetti-banner");
      if (el) el.remove();
      return;
    }
    if (document.getElementById("rrdc-confetti-banner")) return;
    const wrap = document.createElement("div");
    wrap.id = "rrdc-confetti-banner";
    wrap.setAttribute("data-testid", "rrdc-confetti-banner");
    wrap.style.cssText = "position:relative;width:100%;max-width:1100px;margin:24px auto 32px;height:580px;background:#000;border:2px solid #4f46e5;border-radius:14px;box-shadow:0 8px 28px rgba(0,0,0,0.2);overflow:hidden;";
    const inner = document.createElement("div");
    inner.style.cssText = "position:absolute;top:50%;left:50%;width:1920px;height:1080px;transform:translate(-50%,-50%) scale(0.54);transform-origin:center center;";
    const frame = document.createElement("iframe");
    frame.src = "https://rrdcconfetti.com/display";
    frame.title = "RRDC Confetti — Today's Birthdays";
    frame.style.cssText = "width:1920px;height:1080px;border:0;display:block;";
    frame.allow = "autoplay; fullscreen";
    inner.appendChild(frame);
    wrap.appendChild(inner);
    document.body.appendChild(wrap);
    return () => {
      const el = document.getElementById("rrdc-confetti-banner");
      if (el) el.remove();
    };
  }, [isOnMenu]);

  const fetchRoster = useCallback(async (date) => {
    setRosterLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = date ? { date } : {};
      const res = await axios.get(`${API}/roster/today`, { headers: { Authorization: `Bearer ${token}` }, params });
      setRosterData(res.data);
    } catch {
      toast.error("Failed to load roster");
    } finally {
      setRosterLoading(false);
    }
  }, []);

  const fetchAttendanceAlerts = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const [activeRes, todayRes] = await Promise.all([
        axios.get(`${API}/attendance/notifications`, { headers }),
        axios.get(`${API}/attendance/notifications/today`, { headers }),
      ]);
      setAttendanceAlerts(activeRes.data || []);
      setAlertsData(todayRes.data || { active: [], dismissed: [], active_count: 0 });
    } catch { /* non-critical */ }
  }, []);

  const dismissAlert = async (notifId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/attendance/notifications/${notifId}/dismiss`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setAttendanceAlerts(prev => prev.filter(a => a.id !== notifId));
      // Refresh alerts data for the card
      fetchAttendanceAlerts();
    } catch {
      toast.error("Failed to dismiss");
    }
  };

  useEffect(() => {
    fetchAttendanceAlerts();
    const iv = setInterval(fetchAttendanceAlerts, 15000);
    return () => clearInterval(iv);
  }, [fetchAttendanceAlerts]);

  // PM Swim Lessons banner — fetch once and refresh hourly
  useEffect(() => {
    const t = localStorage.getItem("token");
    if (!t) return;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/counselor/pm-swim-lessons`, { headers: { Authorization: `Bearer ${t}` } });
        setPmSwimLessons(res.data || { pm_campers: [], am_campers: [], weekday: "", date: "" });
        // Reset AM dismissal flag at start of a new day
        const today = res.data?.date;
        const lastDismissedDate = localStorage.getItem("am_swim_dismissed_date");
        setAmSwimDismissed(today && lastDismissedDate === today);
      } catch { /* non-critical */ }
    };
    load();
    const iv = setInterval(load, 60 * 60 * 1000);
    return () => clearInterval(iv);
  }, []);

  const dismissAmSwim = () => {
    if (pmSwimLessons?.date) {
      localStorage.setItem("am_swim_dismissed_date", pmSwimLessons.date);
    }
    setAmSwimDismissed(true);
  };

  // Tick once per minute so early-dismissal banners disappear right after
  // the camper's pickup time passes (e.g. 3:20 pickup → cleared at 3:21).
  useEffect(() => {
    const iv = setInterval(() => setNowTick(Date.now()), 30 * 1000);
    return () => clearInterval(iv);
  }, []);

  // Parse a pickup time string like "3:20", "3:20 PM", "15:20", "3:20pm"
  // and return EST {hour, minute} in 24h. Returns null if unparseable.
  const _parsePickup = (s) => {
    if (!s) return null;
    const m = String(s).trim().match(/^(\d{1,2}):(\d{2})\s*([APap][Mm])?$/);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const mm = parseInt(m[2], 10);
    const ampm = (m[3] || "").toLowerCase();
    if (ampm === "pm" && h !== 12) h += 12;
    else if (ampm === "am" && h === 12) h = 0;
    else if (!ampm && h < 8) h += 12; // assume PM for early-dismissal afternoon times
    if (h > 23 || mm > 59) return null;
    return { h, m: mm };
  };

  // Has the pickup time passed in the camp's EST timezone (with 60s grace)?
  const _pickupPassed = (alert, _now) => {
    if (alert?.type !== "early_dismissal") return false;
    const parsed = _parsePickup(alert?.pickup_time);
    if (!parsed) return false;
    const estNow = new Date(new Date().toLocaleString("en-US", { timeZone: "America/New_York" }));
    const cur = estNow.getHours() * 60 + estNow.getMinutes();
    return cur > (parsed.h * 60 + parsed.m);
  };

  const visibleAttendanceAlerts = attendanceAlerts.filter(a => !_pickupPassed(a, nowTick));

  // Proactively check for attendance conflicts on dashboard load
  useEffect(() => {
    const checkConflicts = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/attendance/status`, { headers: { Authorization: `Bearer ${token}` } });
        const newConflicts = res.data.conflicts || {};
        // Only add new conflicts, never remove — resolve action is the only way to clear
        setAttendanceConflicts(prev => {
          const merged = { ...prev };
          Object.keys(newConflicts).forEach(cid => { merged[cid] = newConflicts[cid]; });
          return merged;
        });
      } catch { /* silent */ }
    };
    checkConflicts();
    const iv = setInterval(checkConflicts, 15000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/messages/notifications`, { headers: { Authorization: `Bearer ${token}` } });
        setUnreadMessages(res.data.unread || 0);
      } catch (e) { /* */ }
    };
    fetchUnread();
    const iv = setInterval(fetchUnread, 30000);
    return () => clearInterval(iv);
  }, []);

  // ── Navigator slot-open countdown banner ──
  // Fetches the next upcoming hobby/intensive slot for the counselor's group
  // every 2 minutes. The banner is rendered between (alert_at - now) and slot_open;
  // it auto-hides at 0 and a fresh fetch resets it. Non-Navigator counselors
  // and admins always get null → banner never renders.
  const [upcomingSlot, setUpcomingSlot] = useState(null);
  const [slotNowMs, setSlotNowMs] = useState(Date.now());
  useEffect(() => {
    if (user?.user_type !== "counselor") return;
    const fetchSlot = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/hobbies/upcoming-slot`, { headers: { Authorization: `Bearer ${token}` } });
        setUpcomingSlot(res.data?.slot || null);
      } catch (e) { /* */ }
    };
    fetchSlot();
    const iv = setInterval(fetchSlot, 120000); // every 2 min
    return () => clearInterval(iv);
  }, [user]);
  // 1-second tick for the countdown display
  useEffect(() => {
    if (!upcomingSlot) return;
    const iv = setInterval(() => setSlotNowMs(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [upcomingSlot]);

  // ── Navigator hobbies + intensives reference list ──
  // Fetches the activity catalogs for the head counselor's reference so they
  // can see at a glance what hobbies / intensives the campers are choosing
  // from. Renders inline on the dashboard for Navigator groups only.
  const [navOptions, setNavOptions] = useState(null);
  useEffect(() => {
    const gc = (user?.group_code || "").toUpperCase();
    if (user?.user_type !== "counselor" || !NAV_GROUPS.has(gc)) {
      setNavOptions(null);
      return;
    }
    const fetchOptions = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const [hobRes, intRes] = await Promise.all([
          axios.get(`${API}/hobbies/config?program=navigator`, { headers }).catch(() => null),
          axios.get(`${API}/hobbies/config?program=intensive`, { headers }).catch(() => null),
        ]);
        setNavOptions({
          hobbies: hobRes?.data?.activities || [],
          intensives: intRes?.data?.activities || [],
        });
      } catch (_e) { /* ignore */ }
    };
    fetchOptions();
  }, [user?.group_code, user?.user_type]);

  useEffect(() => {
    const checkSchedule = async () => {
      const gc = user?.group_code;
      if (!gc) { setHasSchedule(false); return; }
      try {
        const token = localStorage.getItem("token");
        await axios.get(`${API}/schedules/${encodeURIComponent(gc)}`, { headers: { Authorization: `Bearer ${token}` } });
        setHasSchedule(true);
      } catch {
        setHasSchedule(false);
      }
    };
    checkSchedule();
  }, [user?.group_code]);

  useEffect(() => {
    // Dashboard lunch tally — background fetch of today's roster to count
    // lunch orders by item for the menu banner.
    const loadLunchTally = async () => {
      if (!isCurrentYear) { setMenuLunchTally(null); return; }
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/roster/today`, { headers: { Authorization: `Bearer ${token}` } });
        const d = res.data || {};
        if (!d.is_camp_day) { setMenuLunchTally(null); return; }
        const kids = (d.campers || []).filter(c => (c.attendance || "").toLowerCase() !== "absent");
        const counts = {};
        let ordered = 0;
        for (const c of kids) {
          const item = (c.lunch_order || "").trim();
          if (item && c.lunch_date === d.date) {
            ordered += 1;
            counts[item] = (counts[item] || 0) + 1;
          }
        }
        const items = Object.entries(counts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
        setMenuLunchTally(kids.length > 0 ? { ordered, notOrdered: kids.length - ordered, items } : null);
      } catch {
        setMenuLunchTally(null);
      }
    };
    loadLunchTally();
  }, [user?.group_code, isCurrentYear]);

  useEffect(() => {
    const sortByLastName = (list) => [...list].sort((a, b) =>
      (a.last_name || "").localeCompare(b.last_name || "") ||
      (a.first_name || "").localeCompare(b.first_name || "")
    );
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const filtered = campers.filter(c => 
        c.first_name?.toLowerCase().includes(query) ||
        c.last_name?.toLowerCase().includes(query) ||
        c.preferred_name?.toLowerCase().includes(query)
      );
      setFilteredCampers(sortByLastName(filtered));
    } else {
      setFilteredCampers(sortByLastName(campers));
    }
  }, [searchQuery, campers]);

  const formatLastSync = (timestamp) => {
    if (!timestamp) return "Never";
    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 60000);
    if (diff < 1) return "Just now";
    if (diff < 60) return `${diff} min ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)} hours ago`;
    return date.toLocaleDateString("en-US", { timeZone: "America/New_York" });
  };

  const handleEvalSubmit = async () => {
    if (!evalForm.head_counselor.trim()) {
      toast.error("Please enter your name");
      return;
    }
    setEvalSubmitting(true);
    try {
      await axios.post(`${API}/evaluations`, evalForm);
      setEvalSubmitted(true);
      toast.success("Evaluation submitted successfully!");
      setShowEvalForm(false);
    } catch (error) {
      toast.error("Failed to submit evaluation");
    } finally {
      setEvalSubmitting(false);
    }
  };

  const handlePrintReport = async () => {
    try {
      const token = localStorage.getItem("token");
      const groupCode = user?.group_code;
      if (!groupCode) { toast.error("No group code"); return; }
      const response = await fetch(`${API}/groups/${groupCode}/report/pdf`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Failed to generate report");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Group_Report_${groupCode}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Group report downloaded!");
    } catch (error) {
      toast.error("Failed to download group report");
    }
  };

  const hasMedicalAlerts = (camper) => {
    const md = camper.medical_data || {};
    // Sectioned format (new): check Medical section
    if (md.Medical) {
      return Object.entries(md.Medical).some(([k, v]) => {
        if (!v) return false;
        const vl = v.toString().toLowerCase();
        return (k.toLowerCase().includes('allerg') || k.toLowerCase().includes('reaction'))
          && vl !== 'no' && vl !== 'none' && vl !== 'n/a' && vl !== '';
      });
    }
    // Flat format (old data)
    return Object.entries(md).some(([k, v]) => {
      if (!v || typeof v === 'object') return false;
      const vl = v.toString().toLowerCase();
      return k.toLowerCase().includes('allerg')
        && vl !== 'no' && vl !== 'none' && vl !== 'n/a' && vl !== '';
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading campers...</p>
        </div>
      </div>
    );
  }

  if (mustSetPw) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
        <HeadPasswordResetModal
          groupCode={user?.group_code || ""}
          groupName={user?.group_name || ""}
          onComplete={() => setMustSetPw(false)}
          onLogout={logout}
        />
      </div>
    );
  }

  if (showBuddySheet) {
    return <BuddyCheckSheet user={user} onBack={() => { setShowBuddySheet(false); setCurrentView("menu"); }} />;
  }

  if (showCarnival) {
    return <CarnivalSnackOrder user={user} onBack={() => setShowCarnival(false)} />;
  }

  if (showMessages) {
    return <MessagesPage user={user} onBack={() => { setShowMessages(false); setCurrentView("menu"); }} />;
  }

  if (showIncidentReport) {
    return <IncidentReportPage group={{ code: user.group_code, name: user.group_name }} onBack={() => { setShowIncidentReport(false); setCurrentView("menu"); }} />;
  }

  if (showEvalForm) {
    // handled inside main return
  }

  if (showTripForm) {
    return <TripAccountabilityForm user={user} onBack={() => { setShowTripForm(false); setCurrentView("menu"); }} />;
  }

  if (showSchedule) {
    return <ScheduleViewer groupCode={user?.group_code} onBack={() => { setShowSchedule(false); setCurrentView("menu"); }} />;
  }

  if (showHobbies) {
    return <HobbiesPage user={user} onBack={() => { setShowHobbies(false); setCurrentView("menu"); }} />;
  }

  if (showAssistantHobbies) {
    return <AssistantHobbiesView user={user} onBack={() => setShowAssistantHobbies(false)} />;
  }

  if (showAssistantIntensives) {
    return <AssistantHobbiesView user={user} program="intensive" onBack={() => setShowAssistantIntensives(false)} />;
  }

  if (showIntensives) {
    return <HobbiesPage user={user} program="intensive" onBack={() => { setShowIntensives(false); setCurrentView("menu"); }} />;
  }

  if (showAttendance) {
    return (
      <AttendanceView
        user={user}
        campers={campers}
        attendanceData={attendanceData}
        setAttendanceData={setAttendanceData}
        attendanceConflicts={attendanceConflicts}
        setAttendanceConflicts={setAttendanceConflicts}
        attendanceSaving={attendanceSaving}
        setAttendanceSaving={setAttendanceSaving}
        onBack={() => { setShowAttendance(false); setCurrentView("menu"); }}
      />
    );
  }

  if (showAlerts) {
    return (
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
          <div className="max-w-2xl mx-auto px-3 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowAlerts(false)} className="px-2" data-testid="alerts-back">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-sm font-bold text-orange-800">Alerts</h1>
                <p className="text-[10px] text-muted-foreground">Early Dismissals & Late Arrivals</p>
              </div>
            </div>
            <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-300">{alertsData.active_count} Active</Badge>
          </div>
        </header>
        <main className="max-w-2xl mx-auto p-3 sm:p-4 space-y-3">
          {alertsData.active.length === 0 && alertsData.dismissed.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground text-sm">No alerts for today</div>
          ) : (
            <>
              {alertsData.active.map(alert => (
                <div key={alert.id} className={`flex items-start gap-3 p-3 rounded-xl border-2 shadow-sm ${alert.type === "early_dismissal" ? "bg-orange-50 border-orange-300" : "bg-blue-50 border-blue-300"}`} data-testid={`alert-active-${alert.id}`}>
                  <div className={`mt-0.5 p-1.5 rounded-full flex-shrink-0 ${alert.type === "early_dismissal" ? "bg-orange-200" : "bg-blue-200"}`}>
                    <Bell className={`w-4 h-4 ${alert.type === "early_dismissal" ? "text-orange-700" : "text-blue-700"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-bold ${alert.type === "early_dismissal" ? "text-orange-900" : "text-blue-900"}`}>
                      {alert.type === "early_dismissal" ? "Early Dismissal" : "Late Arrival"}: {alert.camper_name}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {alert.type === "early_dismissal" ? `Pickup at ${alert.pickup_time || alert.dismissal_time}` : `Arriving at ${alert.pickup_time || alert.dismissal_time}`}
                    </p>
                    {alert.message && <p className="text-xs mt-1 text-gray-600">{alert.message}</p>}
                  </div>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 flex-shrink-0" onClick={() => dismissAlert(alert.id)} data-testid={`alert-dismiss-${alert.id}`}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              {alertsData.dismissed.length > 0 && (
                <>
                  <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider pt-2">Cleared Today</div>
                  {alertsData.dismissed.map(alert => (
                    <div key={alert.id} className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 bg-gray-50 opacity-70" data-testid={`alert-dismissed-${alert.id}`}>
                      <div className="mt-0.5 p-1.5 rounded-full flex-shrink-0 bg-gray-200">
                        <Bell className="w-4 h-4 text-gray-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-600">
                          {alert.type === "early_dismissal" ? "Early Dismissal" : "Late Arrival"}: {alert.camper_name}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {alert.type === "early_dismissal" ? `Pickup at ${alert.pickup_time || alert.dismissal_time}` : `Arriving at ${alert.pickup_time || alert.dismissal_time}`}
                        </p>
                      </div>
                      <Badge variant="outline" className="text-[9px] text-gray-400 border-gray-300">Cleared</Badge>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </main>
      </div>
    );
  }

  // ═══ MENU VIEW ═══
  if (currentView === "menu") {
    return (
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 sm:py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <img src={LOGO_URL} alt="Logo" className="w-8 h-8 sm:w-10 sm:h-10" />
              <div>
                <h1 className="text-sm sm:text-base font-bold text-primary-700">{user?.group_name || user?.group_code}</h1>
                <p className="text-[10px] sm:text-xs text-muted-foreground">Camper Snapshot</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="h-8 px-2 text-xs border rounded-md bg-white font-semibold text-primary-700 cursor-pointer"
                data-testid="counselor-year-selector"
              >
                {availableYears.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <Button variant="ghost" size="sm" onClick={logout} className="text-muted-foreground" data-testid="logout-btn">
                <LogOut className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline text-xs">Logout</span>
              </Button>
            </div>
          </div>
        </header>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4">
          <ColorWarScoreBanner />
          <ScheduleNotificationsBanner groupCode={user?.group_code} />
          <GroupLocationsBanner groupCode={user?.group_code} />
          {!isCurrentYear && (
            <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg" data-testid="read-only-banner">
              <Calendar className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <p className="text-xs font-semibold text-amber-800">Viewing {selectedYear} (Read Only)</p>
            </div>
          )}
          <TodaysBirthdaysBanner campers={campers} />
          {menuLunchTally && (
            <div className="mb-3 p-3 rounded-xl bg-amber-50 border border-amber-200" data-testid="dash-lunch-tally">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-bold text-amber-800" data-testid="dash-lunch-tally-total">
                  Lunch orders today: {menuLunchTally.ordered} ordered
                </span>
                {menuLunchTally.notOrdered > 0 && (
                  <span className="text-xs font-semibold text-amber-700 bg-white border border-amber-300 rounded-full px-2 py-0.5" data-testid="dash-lunch-tally-not-ordered">
                    not ordered: {menuLunchTally.notOrdered}
                  </span>
                )}
              </div>
              {menuLunchTally.items.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {menuLunchTally.items.map(([item, n], i) => (
                    <span key={item} className="text-xs text-amber-900 bg-white border border-amber-200 rounded-md px-2 py-1" data-testid={`dash-lunch-tally-item-${i}`}>
                      {item} <span className="font-bold">{n}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          <h2 className="text-lg font-bold text-center mb-4 text-primary-700">What would you like to do?</h2>
          {/* Navigator hobby/intensive slot-open countdown — appears 10 minutes
              before the logged-in group's selection slot opens, removes at 0. */}
          {upcomingSlot && (() => {
            const remainingMs = (upcomingSlot.slot_open_epoch_ms || 0) - slotNowMs;
            const alertActiveMs = (upcomingSlot.slot_open_epoch_ms || 0) - (upcomingSlot.alert_at_epoch_ms || 0);
            if (remainingMs <= 0 || remainingMs > alertActiveMs) return null;
            const totalSec = Math.max(0, Math.floor(remainingMs / 1000));
            const mm = Math.floor(totalSec / 60);
            const ss = totalSec % 60;
            const label = `${mm}:${String(ss).padStart(2,"0")}`;
            return (
              <div className="mb-4 p-3 rounded-xl border-2 border-violet-500 bg-gradient-to-r from-violet-50 to-fuchsia-50 shadow-md" data-testid="slot-countdown-banner">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-violet-200 flex-shrink-0">
                    <Clock className="w-4 h-4 text-violet-800" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-violet-900">
                      {upcomingSlot.kind === "staggered"
                        ? `Your ${upcomingSlot.program} window opens soon`
                        : `${upcomingSlot.program} window opens soon`}
                    </p>
                    <p className="text-xs text-violet-700">
                      Navigator {upcomingSlot.program}, Week {upcomingSlot.week} — get ready.
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-2xl font-mono font-bold text-violet-800 tabular-nums" data-testid="slot-countdown-time">{label}</div>
                    <div className="text-[10px] text-violet-600 uppercase tracking-wider">until open</div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Navigator hobbies + intensives reference list — only renders for
              B12-B15 / G12-G15 head counselors so they know at a glance what
              their campers are choosing from. */}
          {NAV_GROUPS.has((user?.group_code || "").toUpperCase()) && isCurrentYear && navOptions && (navOptions.hobbies.length > 0 || navOptions.intensives.length > 0) && (
            <div className="mb-4 p-3 rounded-xl border border-violet-200 bg-violet-50/60" data-testid="nav-options-card">
              <div className="text-xs font-bold text-violet-900 mb-2 uppercase tracking-wider">Navigator Selection Options</div>
              <div className="grid grid-cols-2 gap-3">
                <div data-testid="nav-options-hobbies">
                  <div className="text-[10px] font-bold text-fuchsia-700 uppercase mb-1">
                    Hobbies ({navOptions.hobbies.length})
                  </div>
                  <ul className="text-xs text-violet-900 space-y-0.5">
                    {navOptions.hobbies.map(a => <li key={`hob-${a}`}>• {a}</li>)}
                  </ul>
                </div>
                <div data-testid="nav-options-intensives">
                  <div className="text-[10px] font-bold text-amber-700 uppercase mb-1">
                    Intensives ({navOptions.intensives.length})
                  </div>
                  <ul className="text-xs text-violet-900 space-y-0.5">
                    {navOptions.intensives.map(a => <li key={`int-${a}`}>• {a}</li>)}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Attendance Conflict Banner */}
          {Object.keys(attendanceConflicts).length > 0 && (
            <div className="mb-4 p-3 rounded-xl border-2 border-red-500 bg-red-50 shadow-md" data-testid="attendance-conflict-banner">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-bold text-red-800">Attendance Conflict Detected</p>
                  <p className="text-xs text-red-700">{Object.keys(attendanceConflicts).length} camper(s) have a mismatch between Attendance App and Bus App. Open Attendance to review.</p>
                </div>
                <Button size="sm" className="ml-auto bg-red-600 hover:bg-red-700 text-white h-7 text-xs" onClick={() => setShowAttendance(true)} data-testid="attendance-conflict-open-btn">
                  Review
                </Button>
              </div>
            </div>
          )}
          {/* Attendance Alerts */}
          {visibleAttendanceAlerts.length > 0 && (
            <div className="mb-4 space-y-2" data-testid="attendance-alerts">
              {visibleAttendanceAlerts.map(alert => (
                <div key={alert.id} className={`flex items-start gap-3 p-3 rounded-xl border-2 shadow-sm animate-in slide-in-from-top-2 ${alert.type === "early_dismissal" ? "bg-orange-50 border-orange-300" : "bg-blue-50 border-blue-300"}`} data-testid={`attendance-alert-${alert.id}`}>
                  <div className={`mt-0.5 p-1.5 rounded-full flex-shrink-0 ${alert.type === "early_dismissal" ? "bg-orange-200" : "bg-blue-200"}`}>
                    <Bell className={`w-4 h-4 ${alert.type === "early_dismissal" ? "text-orange-700" : "text-blue-700"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-800">
                      {alert.type === "early_dismissal" ? "Early Dismissal" : "Late Arrival"}: {alert.camper_name}
                    </p>
                    {alert.pickup_time && <p className="text-xs font-semibold text-orange-700 mt-0.5">Pickup at {alert.pickup_time}</p>}
                    {alert.dismissal_time && !alert.pickup_time && <p className="text-xs text-slate-600 mt-0.5">Dismissal: {alert.dismissal_time}</p>}
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {alert.date} {alert.reminder_number > 1 ? `(Reminder ${alert.reminder_number})` : ""}
                    </p>
                  </div>
                  {alert.type !== "early_dismissal" && (
                    <button onClick={() => dismissAlert(alert.id)} className="p-1 rounded-md hover:bg-white/60 flex-shrink-0 transition-colors" data-testid={`dismiss-alert-${alert.id}`}>
                      <X className="w-4 h-4 text-slate-400" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          {/* AM Swim Lessons Banner — dismissible, assistants can clear */}
          {pmSwimLessons.am_campers && pmSwimLessons.am_campers.length > 0 && !amSwimDismissed && (
            <div className="mb-4 p-3 rounded-2xl border-2 border-sky-400 bg-gradient-to-r from-sky-50 to-blue-50 shadow-md" data-testid="am-swim-lesson-banner">
              <div className="flex items-start gap-2.5">
                <div className="mt-0.5 p-1.5 rounded-full bg-sky-200 flex-shrink-0">
                  <Droplets className="w-4 h-4 text-sky-800" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-black text-sky-900">
                    Early Swim Today ({pmSwimLessons.weekday})
                  </p>
                  <p className="text-[11px] text-sky-800 mb-1.5">Parents drop these campers at the pool at 8:30 AM — they will NOT be on the AM bus roster.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {pmSwimLessons.am_campers.map(c => (
                      <div key={c.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-white border border-sky-200" data-testid={`am-swim-lesson-row-${c.id}`}>
                        <Droplets className="w-3 h-3 text-sky-500 flex-shrink-0" />
                        <p className="text-xs font-semibold text-slate-800 truncate">{c.first_name} {c.last_name}</p>
                        <span className="ml-auto text-[10px] font-bold text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded">{c.swim_lesson}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <button
                  onClick={dismissAmSwim}
                  className="p-1 rounded-md hover:bg-white/60 flex-shrink-0 transition-colors"
                  aria-label="Dismiss early-swim banner"
                  data-testid="am-swim-lesson-dismiss"
                >
                  <X className="w-4 h-4 text-sky-700" />
                </button>
              </div>
            </div>
          )}

          {/* PM Swim Lessons Banner — non-dismissible, weekday-aware */}
          {pmSwimLessons.pm_campers && pmSwimLessons.pm_campers.length > 0 && (
            <div className="mb-4 p-3 rounded-2xl border-2 border-cyan-500 bg-gradient-to-r from-cyan-50 to-sky-50 shadow-md" data-testid="pm-swim-lesson-banner">
              <div className="flex items-start gap-2.5">
                <div className="mt-0.5 p-1.5 rounded-full bg-cyan-200 flex-shrink-0">
                  <Droplets className="w-4 h-4 text-cyan-800" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-black text-cyan-900">
                    Late Swim Today ({pmSwimLessons.weekday})
                  </p>
                  <p className="text-[11px] text-cyan-800 mb-1.5">Parents pick up at the pool — these campers do NOT come to regular dismissal.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {pmSwimLessons.pm_campers.map(c => (
                      <div key={c.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-white border border-cyan-200" data-testid={`pm-swim-lesson-row-${c.id}`}>
                        <Droplets className="w-3 h-3 text-cyan-500 flex-shrink-0" />
                        <p className="text-xs font-semibold text-slate-800 truncate">{c.first_name} {c.last_name}</p>
                        <span className="ml-auto text-[10px] font-bold text-cyan-700 bg-cyan-100 px-1.5 py-0.5 rounded">{c.swim_lesson}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Daily Deadlines Banner */}
          {isCurrentYear && (
            <div className="bg-red-50 border-2 border-red-300 rounded-2xl p-3 mb-3" data-testid="daily-deadlines-banner">
              <p className="text-xs font-black text-red-900 mb-2">DAILY DEADLINES</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                <div className="bg-white rounded-lg p-1.5 text-center border border-red-200">
                  <p className="text-sm font-bold text-red-700">9:20 AM</p>
                  <p className="text-[9px] text-red-600">Buddy Sheets</p>
                </div>
                <div className="bg-white rounded-lg p-1.5 text-center border border-red-200">
                  <p className="text-sm font-bold text-red-700">9:30 AM</p>
                  <p className="text-[9px] text-red-600">Bus Attendance</p>
                </div>
                <div className="bg-white rounded-lg p-1.5 text-center border border-red-200">
                  <p className="text-sm font-bold text-red-700">9:30 AM</p>
                  <p className="text-[9px] text-red-600">Lunch Orders</p>
                </div>
                <div className="bg-white rounded-lg p-1.5 text-center border border-red-200">
                  <p className="text-sm font-bold text-red-700">10:00 AM</p>
                  <p className="text-[9px] text-red-600">Group Attendance</p>
                </div>
              </div>
              {HOBBY_ELIGIBLE.has((user?.group_code || "").toUpperCase()) && (
                <div className="mt-2 pt-2 border-t border-red-200">
                  <p className="text-[9px] font-bold text-red-800 mb-1">WEEKLY: HOBBIES</p>
                  <p className="text-[9px] text-red-700">Opens <strong>{NAV_GROUPS.has((user?.group_code || "").toUpperCase()) ? "Monday 9:00 AM" : "Monday 12:00 AM"}</strong> — Closes <strong>{NAV_GROUPS.has((user?.group_code || "").toUpperCase()) ? "Thursday 2:00 PM" : "Tuesday 2:00 PM"}</strong></p>
                </div>
              )}
              {NAV_GROUPS.has((user?.group_code || "").toUpperCase()) && (
                <div className="mt-1">
                  <p className="text-[9px] font-bold text-red-800 mb-1">WEEKLY: INTENSIVES</p>
                  <p className="text-[9px] text-red-700">Opens <strong>Monday 9:00 AM</strong> — Closes <strong>Tuesday 2:00 PM</strong></p>
                </div>
              )}
            </div>
          )}
          <MenuSearchBar containerSelector="[data-menu-grid='counselor']" placeholder="Search…" testId="counselor-menu-search" className="mb-3" />
          <ReorderableGrid canReorder={user?.user_type === "counselor" || user?.user_type === "assistant"} menuGridId="counselor" gridClassName="grid grid-cols-2 gap-3 pb-16">
            {/* ▼ Top priority cards — also the ONLY cards an assistant counselor sees ▼ */}

            {/* Group Attendance (external) */}
            <a
              href="http://rrdcattendance.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 p-4 bg-violet-50 rounded-2xl border-2 border-violet-200 hover:border-violet-400 hover:shadow-md transition-all active:scale-95"
              data-testid="menu-attendance"
            >
              <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-violet-700" />
              </div>
              <span className="text-xs font-bold text-violet-800">Group Attendance</span>
            </a>

            {/* Conflict Match */}
            <button
              onClick={() => { if (isCurrentYear) setShowAttendance(true); }}
              className={`flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-emerald-200 transition-all ${isCurrentYear ? "hover:border-emerald-400 hover:shadow-lg" : "opacity-50 cursor-not-allowed"}`}
              data-testid="menu-daily-attendance"
            >
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-700" />
              </div>
              <span className="text-xs font-bold text-emerald-800 leading-tight text-center">Conflict Match</span>
            </button>

            {/* Dining (external) */}
            <a
              href="http://rrdcdining.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 p-4 bg-amber-50 rounded-2xl border-2 border-amber-200 hover:border-amber-400 hover:shadow-md transition-all active:scale-95"
              data-testid="menu-dining"
            >
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Utensils className="w-5 h-5 text-amber-700" />
              </div>
              <span className="text-xs font-bold text-amber-800">Dining</span>
            </a>

            {/* Lunch Menu (image opens in new tab) */}
            <a
              href="https://customer-assets.emergentagent.com/job_ade9caad-2ffa-4ba2-9014-fbe4cea42dcf/artifacts/yg9e7b7o_Summer%202026%20Menu.png"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 p-4 bg-orange-50 rounded-2xl border-2 border-orange-200 hover:border-orange-400 hover:shadow-md transition-all active:scale-95"
              data-testid="menu-lunch-menu"
            >
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <Pizza className="w-5 h-5 text-orange-700" />
              </div>
              <span className="text-xs font-bold text-orange-800">Lunch Menu</span>
            </a>

            {/* Camp Calendar (image opens in new tab) */}
            <a
              href="https://customer-assets.emergentagent.com/job_ade9caad-2ffa-4ba2-9014-fbe4cea42dcf/artifacts/0gl3utt6_Summer%202026%20Calendar.png"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 p-4 bg-sky-50 rounded-2xl border-2 border-sky-200 hover:border-sky-400 hover:shadow-md transition-all active:scale-95"
              data-testid="menu-camp-calendar"
            >
              <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center">
                <CalendarDays className="w-5 h-5 text-sky-700" />
              </div>
              <span className="text-xs font-bold text-sky-800">Camp Calendar</span>
            </a>

            {/* Buddy Sheet */}
            <button
              onClick={() => { if (isCurrentYear) setShowBuddySheet(true); }}
              className={`flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-amber-200 transition-all ${isCurrentYear ? "hover:border-amber-400 hover:shadow-lg" : "opacity-50 cursor-not-allowed"}`}
              data-testid="menu-buddy-sheet"
              aria-disabled={!isCurrentYear}
            >
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-amber-700" />
              </div>
              <span className="text-xs font-bold text-amber-800">Buddy Sheet</span>
            </button>

            {/* Carnival Snack Order — only renders when admin has toggled the
                feature ON. Hidden the rest of the year. */}
            {carnivalEnabled && isCurrentYear && (
              <button
                onClick={() => setShowCarnival(true)}
                className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-pink-200 transition-all hover:border-pink-400 hover:shadow-lg"
                data-testid="menu-carnival-snack"
              >
                <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-pink-700" />
                </div>
                <span className="text-xs font-bold text-pink-800">Carnival Snack</span>
              </button>
            )}

            {/* Trip Accountability — head counselors all groups; assistants only for trip-going groups (B08-B15, G08-G15) */}
            {(!isAssistant || HOBBY_ELIGIBLE.has((user?.group_code || "").toUpperCase())) && (
            <button
              onClick={() => { if (isCurrentYear) setShowTripForm(true); }}
              className={`flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-green-200 transition-all ${isCurrentYear ? "hover:border-green-400 hover:shadow-lg" : "opacity-50 cursor-not-allowed"}`}
              data-testid="menu-trip-form"
              aria-disabled={!isCurrentYear}
            >
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Bus className="w-5 h-5 text-green-700" />
              </div>
              <span className="text-xs font-bold text-green-800">Trip Form</span>
            </button>
            )}

            {/* Daily Roster — visible to both heads and assistants */}
            <button
              onClick={() => { if (isCurrentYear) { fetchRoster(rosterSelectedDate || undefined); setCurrentView("roster"); } }}
              className={`flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-cyan-200 transition-all ${isCurrentYear ? "hover:border-cyan-400 hover:shadow-lg" : "opacity-50 cursor-not-allowed"}`}
              data-testid="menu-daily-roster"
              aria-disabled={!isCurrentYear}
            >
              <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center">
                <CalendarDays className="w-5 h-5 text-cyan-700" />
              </div>
              <span className="text-xs font-bold text-cyan-800">Daily Roster</span>
            </button>

            {/* Group Schedule — visible to heads AND assistants; shows only if
                admin has uploaded a schedule for this group */}
            {hasSchedule && isCurrentYear && (
              <button
                onClick={() => setShowSchedule(true)}
                className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-sky-200 hover:border-sky-400 hover:shadow-lg transition-all active:scale-95"
                data-testid="menu-schedule"
              >
                <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center">
                  <CalendarDays className="w-5 h-5 text-sky-700" />
                </div>
                <span className="text-xs font-bold text-sky-800">Group Schedule</span>
              </button>
            )}

            {/* Intramural Standings — ASSISTANTS in intramural groups see their
                division's standings (read-only). Heads get the full card set below. */}
            {isAssistant && isCurrentYear && INTRAMURAL_BOYS.has((user?.group_code || "").toUpperCase()) && (
              <button onClick={() => setCurrentView("intramural-boys-final-standings")}
                className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-violet-200 hover:border-violet-400 hover:shadow-lg transition-all active:scale-95"
                data-menu-card-label="Intramural Standings"
                data-testid="menu-assistant-boys-standings">
                <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center"><Trophy className="w-5 h-5 text-violet-700" /></div>
                <span className="text-xs font-bold text-violet-800 text-center leading-tight">Intramural Standings</span>
              </button>
            )}
            {isAssistant && isCurrentYear && INTRAMURAL_GIRLS.has((user?.group_code || "").toUpperCase()) && (
              <button onClick={() => setCurrentView("intramural-girls-final-standings")}
                className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-violet-200 hover:border-violet-400 hover:shadow-lg transition-all active:scale-95"
                data-menu-card-label="Intramural Standings"
                data-testid="menu-assistant-girls-standings">
                <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center"><Trophy className="w-5 h-5 text-violet-700" /></div>
                <span className="text-xs font-bold text-violet-800 text-center leading-tight">Intramural Standings</span>
              </button>
            )}
            {isAssistant && isCurrentYear && NAV_GROUPS.has((user?.group_code || "").toUpperCase()) && (
              <button onClick={() => setCurrentView("nav-intramurals")}
                className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-teal-200 hover:border-teal-400 hover:shadow-lg transition-all active:scale-95"
                data-menu-card-label="Nav Intramurals"
                data-testid="menu-assistant-nav-intramurals">
                <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center"><Trophy className="w-5 h-5 text-teal-700" /></div>
                <span className="text-xs font-bold text-teal-800 text-center leading-tight">Nav Intramurals</span>
              </button>
            )}

            {/* Hobbies / Intensives — ASSISTANTS get a view-only version */}
            {isAssistant && isCurrentYear && HOBBY_ELIGIBLE.has((user?.group_code || "").toUpperCase()) && (
              <button onClick={() => setShowAssistantHobbies(true)}
                className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-fuchsia-200 hover:border-fuchsia-400 hover:shadow-lg transition-all active:scale-95"
                data-menu-card-label="Hobbies"
                data-testid="menu-assistant-hobbies">
                <div className="w-10 h-10 rounded-full bg-fuchsia-100 flex items-center justify-center"><Sparkles className="w-5 h-5 text-fuchsia-700" /></div>
                <span className="text-xs font-bold text-fuchsia-800 text-center leading-tight">Hobbies</span>
              </button>
            )}
            {isAssistant && isCurrentYear && NAV_GROUPS.has((user?.group_code || "").toUpperCase()) && (
              <button onClick={() => setShowAssistantIntensives(true)}
                className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-amber-200 hover:border-amber-400 hover:shadow-lg transition-all active:scale-95"
                data-menu-card-label="Intensives"
                data-testid="menu-assistant-intensives">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center"><Sparkles className="w-5 h-5 text-amber-700" /></div>
                <span className="text-xs font-bold text-amber-800 text-center leading-tight">Intensives</span>
              </button>
            )}

            {/* ▼ All other cards — hidden for assistant counselors ▼ */}
            {!isAssistant && <>
            {/* Campers */}
            <button
              onClick={() => setCurrentView("campers")}
              className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-blue-200 hover:border-blue-400 hover:shadow-lg transition-all"
              data-testid="menu-campers"
            >
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-700" />
              </div>
              <span className="text-xs font-bold text-blue-800">Campers</span>
            </button>

            {/* Teams — Team 1 / Team 2 split for 8/10-8/14 (head counselors, B05-B15 / G05-G15 only) */}
            {TEAMS_GROUPS.has((user?.group_code || "").toUpperCase()) && (
            <button
              onClick={() => { if (isCurrentYear) setCurrentView("teams"); }}
              className={`flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-indigo-200 transition-all ${isCurrentYear ? "hover:border-indigo-400 hover:shadow-lg" : "opacity-50 cursor-not-allowed"}`}
              data-testid="menu-teams"
              aria-disabled={!isCurrentYear}
            >
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-indigo-700" />
              </div>
              <span className="text-xs font-bold text-indigo-800">Teams</span>
            </button>
            )}

            {/* Program Evaluation */}
            <button
              onClick={() => { if (isCurrentYear) { setShowEvalForm(true); setCurrentView("campers"); } }}
              className={`flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-purple-200 transition-all ${isCurrentYear ? "hover:border-purple-400 hover:shadow-lg" : "opacity-50 cursor-not-allowed"}`}
              data-testid="menu-eval-form"
              aria-disabled={!isCurrentYear}
            >
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Star className="w-5 h-5 text-purple-700" />
              </div>
              <span className="text-xs font-bold text-purple-800 leading-tight">End of Season Evaluation</span>
            </button>

            {/* Messages */}
            <button
              onClick={() => { if (isCurrentYear) setShowMessages(true); }}
              className={`relative flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-teal-200 transition-all ${isCurrentYear ? "hover:border-teal-400 hover:shadow-lg" : "opacity-50 cursor-not-allowed"}`}
              data-testid="menu-messages"
              aria-disabled={!isCurrentYear}
            >
              <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-teal-700" />
              </div>
              <span className="text-xs font-bold text-teal-800">Messages</span>
              {isCurrentYear && unreadMessages > 0 && (
                <span className="absolute top-2 right-2 bg-red-500 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {unreadMessages}
                </span>
              )}
            </button>

            {/* Incident Report */}
            <button
              onClick={() => { if (isCurrentYear) { setShowIncidentReport(true); setCurrentView("incident"); } }}
              className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all ${isCurrentYear ? "bg-red-50 border-red-200 hover:border-red-400 hover:shadow-md active:scale-95" : "bg-red-50 border-red-200 opacity-50 cursor-not-allowed"}`}
              data-testid="menu-incident-report"
              aria-disabled={!isCurrentYear}
            >
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-700" />
              </div>
              <span className="text-xs font-bold text-red-800">Incident Report</span>
            </button>

            {/* Conflict Match — moved to top priority section */}

            {/* Alerts — Early Dismissals & Late Arrivals */}
            <button
              onClick={() => { if (isCurrentYear) { fetchAttendanceAlerts(); setShowAlerts(true); } }}
              className={`relative flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-orange-200 transition-all ${isCurrentYear ? "hover:border-orange-400 hover:shadow-lg" : "opacity-50 cursor-not-allowed"}`}
              data-testid="menu-alerts"
            >
              {alertsData.active_count > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="alerts-badge">{alertsData.active_count}</span>
              )}
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <Bell className="w-5 h-5 text-orange-700" />
              </div>
              <span className="text-xs font-bold text-orange-800 leading-tight text-center">Alerts</span>
            </button>

            {/* Dining + Group Attendance — moved to top priority section */}

            {/* Group Schedule — moved to the shared top section (visible to assistants) */}

            {/* Hobbies Pick of the Week — only for eligible groups */}
            {HOBBY_ELIGIBLE.has((user?.group_code || "").toUpperCase()) && isCurrentYear && (
              <button
                onClick={() => setShowHobbies(true)}
                className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-fuchsia-200 hover:border-fuchsia-400 hover:shadow-lg transition-all active:scale-95"
                data-testid="menu-hobbies"
              >
                <div className="w-10 h-10 rounded-full bg-fuchsia-100 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-fuchsia-700" />
                </div>
                <span className="text-xs font-bold text-fuchsia-800">Hobbies</span>
              </button>
            )}

            {/* Intensives — only for Navigator groups */}
            {NAV_GROUPS.has((user?.group_code || "").toUpperCase()) && isCurrentYear && (
              <button
                onClick={() => setShowIntensives(true)}
                className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-amber-200 hover:border-amber-400 hover:shadow-lg transition-all active:scale-95"
                data-testid="menu-intensives"
              >
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-amber-700" />
                </div>
                <span className="text-xs font-bold text-amber-800">Intensives</span>
              </button>
            )}

            {/* Paperwork Submitted — CIT only */}
            {(user?.group_code || "").toUpperCase() === "CIT" && isCurrentYear && (
              <button
                onClick={() => {
                  setPaperworkLoading(true);
                  const t = localStorage.getItem("token");
                  axios.get(`${API}/cit/paperwork`, { headers: { Authorization: `Bearer ${t}` } })
                    .then(res => setPaperworkData(res.data))
                    .catch(() => toast.error("Failed to load paperwork"))
                    .finally(() => setPaperworkLoading(false));
                  setCurrentView("paperwork");
                }}
                className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-indigo-200 hover:border-indigo-400 hover:shadow-lg transition-all active:scale-95"
                data-testid="menu-paperwork"
              >
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-indigo-700" />
                </div>
                <span className="text-xs font-bold text-indigo-800">Paperwork Submitted</span>
              </button>
            )}

            {/* CIT Trips — CIT only */}
            {(user?.group_code || "").toUpperCase() === "CIT" && isCurrentYear && (
              <button
                onClick={() => {
                  setCitTripsLoading(true);
                  const t = localStorage.getItem("token");
                  axios.get(`${API}/cit/trips`, { headers: { Authorization: `Bearer ${t}` } })
                    .then(res => setCitTripsData(res.data))
                    .catch(() => toast.error("Failed to load trips"))
                    .finally(() => setCitTripsLoading(false));
                  setCurrentView("cit-trips");
                }}
                className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-orange-200 hover:border-orange-400 hover:shadow-lg transition-all active:scale-95"
                data-testid="menu-cit-trips"
              >
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-orange-700" />
                </div>
                <span className="text-xs font-bold text-orange-800">CIT Trips</span>
              </button>
            )}

            {/* CIT Choice / Program — CIT only */}
            {(user?.group_code || "").toUpperCase() === "CIT" && isCurrentYear && (
              <button
                onClick={() => {
                  setCitChoicesLoading(true);
                  const t = localStorage.getItem("token");
                  axios.get(`${API}/cit/choices`, { headers: { Authorization: `Bearer ${t}` } })
                    .then(res => setCitChoicesData(res.data))
                    .catch(() => toast.error("Failed to load CIT choices"))
                    .finally(() => setCitChoicesLoading(false));
                  setCurrentView("cit-choices");
                }}
                className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-violet-200 hover:border-violet-400 hover:shadow-lg transition-all active:scale-95"
                data-testid="menu-cit-choices"
              >
                <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
                  <Star className="w-5 h-5 text-violet-700" />
                </div>
                <span className="text-xs font-bold text-violet-800 text-center leading-tight">CIT Choice / Program</span>
              </button>
            )}

            {/* Overnight Trip — CIT only */}
            {(user?.group_code || "").toUpperCase() === "CIT" && isCurrentYear && (
              <button
                onClick={() => {
                  setCitOvernightLoading(true);
                  const t = localStorage.getItem("token");
                  axios.get(`${API}/cit/overnight`, { headers: { Authorization: `Bearer ${t}` } })
                    .then(res => setCitOvernightData(res.data))
                    .catch(() => toast.error("Failed to load overnight list"))
                    .finally(() => setCitOvernightLoading(false));
                  setCurrentView("cit-overnight");
                }}
                className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-slate-300 hover:border-slate-500 hover:shadow-lg transition-all active:scale-95"
                data-testid="menu-cit-overnight"
              >
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                  <Moon className="w-5 h-5 text-slate-700" />
                </div>
                <span className="text-xs font-bold text-slate-800 text-center leading-tight">Overnight & Broadway</span>
              </button>
            )}

            {/* Intramural Cards — Boys groups */}
            {INTRAMURAL_BOYS.has((user?.group_code || "").toUpperCase()) && isCurrentYear && (
              <>
                <button onClick={() => setCurrentView("intramural-boys-teams")}
                  className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-blue-200 hover:border-blue-400 hover:shadow-lg transition-all active:scale-95"
                  data-testid="menu-boys-teams">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center"><Trophy className="w-5 h-5 text-blue-700" /></div>
                  <span className="text-xs font-bold text-blue-800 text-center leading-tight">Boys Intramural Teams</span>
                </button>
                <button onClick={() => setCurrentView("intramural-boys-schedule")}
                  className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-emerald-200 hover:border-emerald-400 hover:shadow-lg transition-all active:scale-95"
                  data-testid="menu-boys-schedule">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center"><CalendarDays className="w-5 h-5 text-emerald-700" /></div>
                  <span className="text-xs font-bold text-emerald-800 text-center leading-tight">Boys Schedule</span>
                </button>
                <button onClick={() => setCurrentView("intramural-boys-playoffs")}
                  className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-amber-200 hover:border-amber-400 hover:shadow-lg transition-all active:scale-95"
                  data-testid="menu-boys-playoffs">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center"><Swords className="w-5 h-5 text-amber-700" /></div>
                  <span className="text-xs font-bold text-amber-800 text-center leading-tight">Boys Playoffs</span>
                </button>
                <button onClick={() => setCurrentView("intramural-boys-final-standings")}
                  className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-violet-200 hover:border-violet-400 hover:shadow-lg transition-all active:scale-95"
                  data-testid="menu-boys-final-standings">
                  <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center"><Trophy className="w-5 h-5 text-violet-700" /></div>
                  <span className="text-xs font-bold text-violet-800 text-center leading-tight">Intramural Final Standings</span>
                </button>
              </>
            )}

            {/* Intramural Cards — Girls groups */}
            {INTRAMURAL_GIRLS.has((user?.group_code || "").toUpperCase()) && isCurrentYear && (
              <>
                <button onClick={() => setCurrentView("intramural-girls-teams")}
                  className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-rose-200 hover:border-rose-400 hover:shadow-lg transition-all active:scale-95"
                  data-testid="menu-girls-teams">
                  <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center"><Trophy className="w-5 h-5 text-rose-700" /></div>
                  <span className="text-xs font-bold text-rose-800 text-center leading-tight">Girls Intramural Teams</span>
                </button>
                <button onClick={() => setCurrentView("intramural-girls-schedule")}
                  className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-emerald-200 hover:border-emerald-400 hover:shadow-lg transition-all active:scale-95"
                  data-testid="menu-girls-schedule">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center"><CalendarDays className="w-5 h-5 text-emerald-700" /></div>
                  <span className="text-xs font-bold text-emerald-800 text-center leading-tight">Girls Schedule</span>
                </button>
                <button onClick={() => setCurrentView("intramural-girls-playoffs")}
                  className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-amber-200 hover:border-amber-400 hover:shadow-lg transition-all active:scale-95"
                  data-testid="menu-girls-playoffs">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center"><Swords className="w-5 h-5 text-amber-700" /></div>
                  <span className="text-xs font-bold text-amber-800 text-center leading-tight">Girls Playoffs</span>
                </button>
                <button onClick={() => setCurrentView("intramural-girls-final-standings")}
                  className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-violet-200 hover:border-violet-400 hover:shadow-lg transition-all active:scale-95"
                  data-testid="menu-girls-final-standings">
                  <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center"><Trophy className="w-5 h-5 text-violet-700" /></div>
                  <span className="text-xs font-bold text-violet-800 text-center leading-tight">Intramural Final Standings</span>
                </button>
              </>
            )}

            {/* Navigator Intramural Cards — B12-B15, G12-G15 */}
            {NAV_GROUPS.has((user?.group_code || "").toUpperCase()) && isCurrentYear && (
              <button onClick={() => setCurrentView("nav-intramurals")}
                className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border-2 border-teal-200 hover:border-teal-400 hover:shadow-lg transition-all active:scale-95"
                data-testid="menu-nav-intramurals">
                <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center"><Trophy className="w-5 h-5 text-teal-700" /></div>
                <span className="text-xs font-bold text-teal-800 text-center leading-tight">Nav Intramurals</span>
              </button>
            )}
            </>}
          </ReorderableGrid>
        </div>
      </div>
    );
  }

  // ═══ TEAMS VIEW (Team 1 / Team 2 split — head counselors) ═══
  if (currentView === "teams") {
    return <TeamsView user={user} onBack={() => setCurrentView("menu")} />;
  }

  // ═══ INTRAMURAL VIEWS (read-only) ═══
  if (currentView === "intramural-boys-teams") {
    const t = localStorage.getItem("token");
    return <TeamSorterView division="boys" headers={{ Authorization: `Bearer ${t}` }} onBack={() => setCurrentView("menu")} isEditable={false} />;
  }
  if (currentView === "intramural-girls-teams") {
    const t = localStorage.getItem("token");
    return <TeamSorterView division="girls" headers={{ Authorization: `Bearer ${t}` }} onBack={() => setCurrentView("menu")} isEditable={false} />;
  }
  if (currentView === "intramural-boys-schedule") {
    const t = localStorage.getItem("token");
    return <ScorecardView headers={{ Authorization: `Bearer ${t}` }} onBack={() => setCurrentView("menu")} isEditable={false} />;
  }
  if (currentView === "intramural-girls-schedule") {
    const t = localStorage.getItem("token");
    return <ScorecardView headers={{ Authorization: `Bearer ${t}` }} onBack={() => setCurrentView("menu")} isEditable={false} />;
  }
  if (currentView === "intramural-boys-playoffs") {
    const t = localStorage.getItem("token");
    return <PlayoffsView division="boys" headers={{ Authorization: `Bearer ${t}` }} onBack={() => setCurrentView("menu")} isEditable={false} />;
  }
  if (currentView === "intramural-girls-playoffs") {
    const t = localStorage.getItem("token");
    return <PlayoffsView division="girls" headers={{ Authorization: `Bearer ${t}` }} onBack={() => setCurrentView("menu")} isEditable={false} />;
  }
  if (currentView === "intramural-boys-final-standings") {
    const t = localStorage.getItem("token");
    return <FinalStandingsView headers={{ Authorization: `Bearer ${t}` }} onBack={() => setCurrentView("menu")} lockDivision={isAssistant ? "boys" : null} />;
  }
  if (currentView === "intramural-girls-final-standings") {
    const t = localStorage.getItem("token");
    return <FinalStandingsView headers={{ Authorization: `Bearer ${t}` }} onBack={() => setCurrentView("menu")} lockDivision={isAssistant ? "girls" : null} />;
  }

  // ═══ NAVIGATOR INTRAMURAL VIEWS (read-only) ═══
  if (currentView === "nav-intramurals") {
    const t = localStorage.getItem("token");
    return <NavigatorIntramuralsLanding headers={{ Authorization: `Bearer ${t}` }} onBack={() => setCurrentView("menu")} isEditable={false} />;
  }

  // ═══ CIT TRIPS VIEW ═══
  if (currentView === "cit-trips") {
    const tripCampers = citTripsData?.campers || [];
    const tripKeys = ["Trip 1", "Trip 2", "Trip 3", "Trip 4", "Trip 5", "Trip 6", "Trip 7", "Trip 8"];
    return (
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 sm:py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <Button variant="ghost" size="sm" onClick={() => setCurrentView("menu")} className="px-2 gap-1" data-testid="cit-trips-back-btn">
                <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Dashboard</span>
              </Button>
              <div>
                <h1 className="text-sm sm:text-base font-bold text-orange-700">CIT Trips</h1>
                <p className="text-xs text-muted-foreground">Trip enrollment by camper</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-8"
              data-testid="cit-trips-sync-btn"
              onClick={async () => {
                const t = localStorage.getItem("token");
                const tid = toast.loading("Syncing CIT trips from CampMinder…");
                try {
                  // Synchronous — backend returns real per-CIT changes now.
                  const syncRes = await axios.post(
                    `${API}/sync/cit-trips-fields`, {},
                    { headers: { Authorization: `Bearer ${t}` }, timeout: 180000 }
                  );
                  const d = syncRes.data || {};
                  const updated = d.updated || 0;
                  const examined = d.examined || 0;
                  const errors = d.errors || [];
                  const changes = d.changes || [];
                  // Refresh the trip grid immediately — no need to wait
                  const res = await axios.get(`${API}/cit/trips`, { headers: { Authorization: `Bearer ${t}` } });
                  setCitTripsData(res.data);
                  if (errors.length) {
                    toast.warning(
                      `Synced ${updated}/${examined} · ${errors.length} CIT${errors.length !== 1 ? "s" : ""} failed CampMinder lookup`,
                      { id: tid, duration: 12000 }
                    );
                  } else if (updated === 0) {
                    toast.success(`No changes — all ${examined} CITs already in sync`, { id: tid });
                  } else {
                    // Show first 3 changed names so the user knows what moved.
                    const names = changes.slice(0, 3).map(c => c.name).filter(Boolean).join(", ");
                    const more = changes.length > 3 ? ` +${changes.length - 3} more` : "";
                    toast.success(
                      `Updated ${updated}/${examined} CITs${names ? `: ${names}${more}` : ""}`,
                      { id: tid, duration: 10000 }
                    );
                  }
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Sync failed", { id: tid });
                }
              }}
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Sync
            </Button>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-4 space-y-3">
          {citTripsLoading ? (
            <div className="text-center py-10 text-muted-foreground">Loading...</div>
          ) : (
            <>
              <div className="mb-3 p-3 bg-orange-50 border border-orange-200 rounded-lg" data-testid="cit-trips-summary">
                <p className="text-sm font-medium text-orange-800">{citTripsData?.total || 0} CIT campers</p>
              </div>
              <div className="space-y-2">
                {tripCampers.map((c) => (
                  <div key={c.id} className="p-3 rounded-lg border bg-white" data-testid={`cit-trip-camper-${c.id}`}>
                    <p className="text-sm font-semibold mb-2">{c.name}</p>
                    <div className="flex flex-wrap gap-2">
                      {tripKeys.map((tk) => {
                        const val = c.trips[tk];
                        return (
                          <span
                            key={tk}
                            className={`inline-flex items-center text-[11px] px-2 py-1 rounded-md font-medium ${
                              val === true ? "bg-green-100 text-green-800 border border-green-300" :
                              val === false ? "bg-red-50 text-red-600 border border-red-200" :
                              "bg-gray-50 text-gray-400 border border-gray-200"
                            }`}
                          >
                            {tk}: {val === true ? "Yes" : val === false ? "No" : "—"}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </main>
      </div>
    );
  }

  // ═══ OVERNIGHT & BROADWAY (CIT only) ═══
  if (currentView === "cit-overnight") {
    return (
      <TripRostersView
        data={citOvernightData}
        loading={citOvernightLoading}
        onBack={() => setCurrentView("menu")}
        headerSubtitle="CIT trip rosters"
      />
    );
  }

  // ═══ PAPERWORK VIEW (CIT only) ═══
  if (currentView === "paperwork") {
    const camperList = paperworkData?.campers || [];
    return (
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 sm:py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <Button variant="ghost" size="sm" onClick={() => setCurrentView("menu")} className="px-2 gap-1" data-testid="paperwork-back-btn">
                <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Dashboard</span>
              </Button>
              <div>
                <h1 className="text-sm sm:text-base font-bold text-indigo-700">Paperwork Submitted</h1>
                <p className="text-xs text-muted-foreground">CIT Parent Signature</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-8"
              data-testid="paperwork-sync-btn"
              onClick={async () => {
                const t = localStorage.getItem("token");
                try {
                  await axios.post(`${API}/sync/cit-paperwork`, {}, { headers: { Authorization: `Bearer ${t}` } });
                  toast.success("Syncing CIT paperwork from CampMinder...");
                  setTimeout(async () => {
                    const res = await axios.get(`${API}/cit/paperwork`, { headers: { Authorization: `Bearer ${t}` } });
                    setPaperworkData(res.data);
                  }, 10000);
                } catch { toast.error("Sync failed"); }
              }}
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Sync
            </Button>
          </div>
        </header>
        <main className="max-w-3xl mx-auto px-4 sm:px-6 py-4 space-y-3">
          {paperworkLoading ? (
            <div className="text-center py-10 text-muted-foreground">Loading...</div>
          ) : (
            <>
              <div className="mb-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="paperwork-summary">
                <p className="text-sm font-medium text-indigo-800">
                  {paperworkData?.submitted_count || 0} of {paperworkData?.total || 0} submitted
                </p>
              </div>
              <div className="space-y-2">
                {camperList.map((c) => (
                  <div
                    key={c.id}
                    className={`p-3 rounded-lg border ${c.submitted ? "bg-white border-green-200" : "bg-gray-50 border-gray-200"}`}
                    data-testid={`paperwork-camper-${c.id}`}
                  >
                    <p className="text-sm font-semibold">{c.name}</p>
                    {c.submitted ? (
                      <p className="text-xs text-green-700 mt-0.5">Date Paperwork Submitted: {c.paperwork_date}</p>
                    ) : (
                      <p className="text-xs text-red-500 mt-0.5 italic">Not yet submitted</p>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </main>
      </div>
    );
  }

  // ═══ CIT CHOICE / PROGRAM VIEW (CIT only) ═══
  if (currentView === "cit-choices") {
    const list = citChoicesData?.campers || [];
    const filtered = list.filter(c => {
      if (!citChoicesSearch.trim()) return true;
      const q = citChoicesSearch.toLowerCase();
      return `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase().includes(q)
        || (c.cit_subgroup || "").toLowerCase().includes(q);
    });
    const grouped = filtered.reduce((acc, c) => {
      const k = c.cit_subgroup || "CIT";
      if (!acc[k]) acc[k] = [];
      acc[k].push(c);
      return acc;
    }, {});
    const subgroupOrder = Object.keys(grouped).sort();
    return (
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 sm:py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <Button variant="ghost" size="sm" onClick={() => setCurrentView("menu")} className="px-2 gap-1" data-testid="cit-choices-back-btn">
                <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Dashboard</span>
              </Button>
              <div>
                <h1 className="text-sm sm:text-base font-bold text-violet-700">CIT Choice / Program</h1>
                <p className="text-xs text-muted-foreground">{citChoicesData?.total || 0} CITs · synced from CampMinder</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={logout} className="text-muted-foreground" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </header>
        <main className="max-w-5xl mx-auto px-4 sm:px-6 py-4 space-y-3">
          {citChoicesLoading ? (
            <div className="text-center py-10 text-muted-foreground">Loading...</div>
          ) : (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or subgroup..."
                  value={citChoicesSearch}
                  onChange={(e) => setCitChoicesSearch(e.target.value)}
                  className="pl-9"
                  data-testid="cit-choices-search"
                />
              </div>

              {filtered.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No CITs match.</p>
              ) : (
                subgroupOrder.map(sub => (
                  <section key={sub} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading font-semibold text-base text-violet-700">{sub}</h3>
                      <Badge variant="outline" className="text-[10px]">{grouped[sub].length}</Badge>
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {grouped[sub].map(c => {
                        const hasAny = c.cit_choice_1 || c.cit_choice_2 || c.cit_choice_3
                          || c.cit_program_1 || c.cit_program_2 || c.cit_program_3;
                        return (
                          <button
                            key={c.id}
                            onClick={() => setCitChoicesSelected(c)}
                            className="text-left p-3 rounded-lg border bg-white hover:bg-violet-50 hover:border-violet-300 transition-colors"
                            data-testid={`cit-choice-row-${c.id}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <p className="font-semibold text-sm">{c.first_name} {c.last_name}</p>
                              {!hasAny && (
                                <Badge variant="outline" className="text-[9px] border-amber-300 text-amber-700 bg-amber-50">No data</Badge>
                              )}
                            </div>
                            {c.cit_last_year_position && (
                              <p className="text-[11px] text-muted-foreground mt-0.5 truncate">Last yr: {c.cit_last_year_position}</p>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </section>
                ))
              )}
            </>
          )}
        </main>

        <Dialog open={!!citChoicesSelected} onOpenChange={() => setCitChoicesSelected(null)}>
          <DialogContent className="max-w-xl" data-testid="cit-choices-modal">
            {citChoicesSelected && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-violet-700">
                    <Star className="w-5 h-5" />
                    {citChoicesSelected.first_name} {citChoicesSelected.last_name}
                  </DialogTitle>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    <Badge variant="outline" className="text-[10px]">{citChoicesSelected.cit_subgroup || "CIT"}</Badge>
                    {citChoicesSelected.cit_last_year_position && (
                      <Badge variant="outline" className="text-[10px] bg-slate-50">Last yr: {citChoicesSelected.cit_last_year_position}</Badge>
                    )}
                    <Badge variant="outline" className={`text-[10px] ${citChoicesSelected.cit_gossip_signed ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-rose-50 text-rose-700 border-rose-300"}`}>
                      Gossip form: {citChoicesSelected.cit_gossip_signed ? "Signed" : "Not signed"}
                    </Badge>
                  </div>
                </DialogHeader>
                <div className="space-y-3 pt-2">
                  {[1, 2, 3].map(rank => {
                    const program = citChoicesSelected[`cit_program_${rank}`] || "";
                    const choice = citChoicesSelected[`cit_choice_${rank}`] || "";
                    if (!program && !choice) {
                      return (
                        <div key={rank} className="p-3 rounded-lg border bg-slate-50/50 border-slate-200">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Preference {rank}</p>
                          <p className="text-sm text-muted-foreground italic">— not provided —</p>
                        </div>
                      );
                    }
                    return (
                      <div key={rank} className="p-3 rounded-lg border bg-violet-50/40 border-violet-200" data-testid={`cit-preference-${rank}`}>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-violet-700 mb-1.5">Preference {rank}</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Program</p>
                            <p className="text-sm font-medium">{program || <span className="text-muted-foreground italic">—</span>}</p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Choice</p>
                            <p className="text-sm font-medium">{choice || <span className="text-muted-foreground italic">—</span>}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ═══ ROSTER VIEW ═══
  if (currentView === "roster") {
    const rosterDate = rosterData?.date ? new Date(rosterData.date + "T12:00:00") : new Date();
    const dateLabel = rosterDate.toLocaleDateString("en-US", { timeZone: "America/New_York", weekday: "long", month: "long", day: "numeric", year: "numeric" });
    const rosterCampers = [...(rosterData?.campers || [])]
      .filter(c => (c.attendance || "").toLowerCase() !== "absent")
      .sort((a, b) =>
      (a.last_name || "").localeCompare(b.last_name || "") ||
      (a.first_name || "").localeCompare(b.first_name || "")
    );
    const noData = !rosterData?.is_camp_day || rosterCampers.length === 0;

    // Trip-day flags — Navigator/CIT optional trips
    const dateStr = rosterData?.date || "";
    const isOvernightDay = OVERNIGHT_TRIP_DATES.includes(dateStr);
    const isBroadwayDay = dateStr === BROADWAY_TRIP_DATE;
    const overnightCount = isOvernightDay ? rosterCampers.filter(c => c.overnight_trip).length : 0;
    const broadwayCount = isBroadwayDay ? rosterCampers.filter(c => c.broadway_trip).length : 0;

    // Lunch tally — counts today's lunch_order values already on the roster
    // (same lunch_date check the camper cards use). Frontend-only, own group.
    const lunchTally = (() => {
      const counts = {};
      let ordered = 0;
      for (const c of rosterCampers) {
        const item = (c.lunch_order || "").trim();
        if (item && c.lunch_date === dateStr) {
          ordered += 1;
          counts[item] = (counts[item] || 0) + 1;
        }
      }
      const items = Object.entries(counts)
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
      return { ordered, notOrdered: rosterCampers.length - ordered, items };
    })();

    return (
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 sm:py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <Button variant="ghost" size="sm" onClick={() => setCurrentView("menu")} className="px-2 gap-1" data-testid="roster-back-btn">
                <ArrowLeft className="w-4 h-4" />
                <span className="text-xs font-medium">Dashboard</span>
              </Button>
              <div>
                <h1 className="text-sm sm:text-base font-bold text-cyan-700">Daily Roster</h1>
                <p className="text-[10px] sm:text-xs text-muted-foreground">{user?.group_name || user?.group_code}</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={logout} className="text-muted-foreground" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </header>
        <main className="max-w-7xl mx-auto p-4 sm:p-5 md:p-6">
          <div className="mb-4 p-3 bg-cyan-50 border border-cyan-200 rounded-lg" data-testid="roster-header-info">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h2 className="font-bold text-base text-cyan-800">
                  Roster for {dateLabel}
                </h2>
                <p className="text-sm text-cyan-600">
                  {rosterLoading ? "Loading..." : noData ? "No campers scheduled for this date." : (() => {
                    const absentCount = (rosterData?.campers || []).filter(c => (c.attendance || "").toLowerCase() === "absent").length;
                    return `${rosterCampers.length} camper${rosterCampers.length !== 1 ? "s" : ""}${absentCount > 0 ? ` (${absentCount} absent — hidden)` : ""}`;
                  })()}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-cyan-700">View date:</label>
                <input
                  type="date"
                  min="2026-06-29"
                  max="2026-08-20"
                  value={rosterSelectedDate || rosterData?.date || ""}
                  onChange={(e) => { setRosterSelectedDate(e.target.value); fetchRoster(e.target.value); }}
                  className="px-2 py-1 text-sm border border-cyan-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  data-testid="roster-date-picker"
                />
              </div>
            </div>
          </div>
          {rosterLoading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading roster...</p>
            </div>
          ) : noData ? (
            <div className="text-center py-16">
              <CalendarDays className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="text-xl text-muted-foreground mb-2">No campers scheduled for this date.</h3>
              <p className="text-sm text-muted-foreground">
                {!rosterData?.is_camp_day ? "This is not a camp operating day." : "No campers match the session filter for this date."}
              </p>
            </div>
          ) : (
            <>
              {isOvernightDay && overnightCount > 0 && (
                <div className="mb-4 p-4 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-700 text-white shadow-lg flex items-center gap-3" data-testid="overnight-trip-banner">
                  <Moon className="w-6 h-6 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="font-heading font-bold text-base">Overnight Trip Today</div>
                    <div className="text-xs text-indigo-100">{overnightCount} camper{overnightCount !== 1 ? "s" : ""} from this group going on the overnight. Check badges below.</div>
                  </div>
                </div>
              )}
              {isBroadwayDay && broadwayCount > 0 && (
                <div className="mb-4 p-4 rounded-xl bg-gradient-to-r from-pink-500 to-rose-600 text-white shadow-lg flex items-center gap-3" data-testid="broadway-trip-banner">
                  <Drama className="w-6 h-6 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="font-heading font-bold text-base">Broadway Trip Today</div>
                    <div className="text-xs text-pink-100">{broadwayCount} camper{broadwayCount !== 1 ? "s" : ""} from this group going to Broadway. Check badges below.</div>
                  </div>
                </div>
              )}
              <div className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200" data-testid="lunch-tally-block">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-amber-800" data-testid="lunch-tally-total">
                    Lunch orders today: {lunchTally.ordered} ordered
                  </span>
                  {lunchTally.notOrdered > 0 && (
                    <span className="text-xs font-semibold text-amber-700 bg-white border border-amber-300 rounded-full px-2 py-0.5" data-testid="lunch-tally-not-ordered">
                      not ordered: {lunchTally.notOrdered}
                    </span>
                  )}
                </div>
                {lunchTally.items.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {lunchTally.items.map(([item, n], i) => (
                      <span key={item} className="text-xs text-amber-900 bg-white border border-amber-200 rounded-md px-2 py-1" data-testid={`lunch-tally-item-${i}`}>
                        {item} <span className="font-bold">{n}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                {rosterCampers.map((camper) => {
                  const camperAlerts = attendanceAlerts.filter(a => a.camper_id === camper.id);
                  return (
                    <RosterCamperCard
                      key={camper.id}
                      camper={camper}
                      hasAlerts={hasMedicalAlerts(camper)}
                      camperAlerts={camperAlerts}
                      onClick={isAssistant ? null : () => setSelectedCamper(camper)}
                      isAssistant={isAssistant}
                      isNursery={(user?.group_code || "").toUpperCase().startsWith("N")}
                      rosterDate={rosterData?.date || ""}
                      isOvernightDay={isOvernightDay}
                      isBroadwayDay={isBroadwayDay}
                    />
                  );
                })}
              </div>
            </>
          )}
        </main>
        <Dialog open={!isAssistant && !!selectedCamper} onOpenChange={() => setSelectedCamper(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] p-0 w-[calc(100%-2rem)] sm:w-full rounded-xl" aria-describedby="roster-camper-detail-description">
            <span id="roster-camper-detail-description" className="sr-only">Camper roster details</span>
            {selectedCamper && (
              <CamperDetail camper={selectedCamper} customFields={customFields} onClose={() => setSelectedCamper(null)} readOnly={!isCurrentYear} userGroupCode={user?.group_code} />
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ═══ CAMPERS VIEW (original dashboard) ═══

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 sm:py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => setCurrentView("menu")} className="px-2 gap-1 flex-shrink-0" data-testid="back-to-menu">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-xs font-medium">Dashboard</span>
            </Button>
            {isCurrentYear && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1 text-xs" data-testid="actions-dropdown">
                    Actions <ChevronDown className="w-3.5 h-3.5" />
                    {unreadMessages > 0 && (
                      <span className="ml-1 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                        {unreadMessages}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuItem onClick={() => setShowBuddySheet(true)} data-testid="action-buddy-sheet">
                    <Droplets className="w-4 h-4 mr-2 text-blue-600" />
                    Buddy Check Sheet
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handlePrintReport} data-testid="action-print-report">
                    <Printer className="w-4 h-4 mr-2 text-gray-600" />
                    Print Report
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowTripForm(true)} data-testid="action-trip-form">
                    <MapPin className="w-4 h-4 mr-2 text-teal-600" />
                    Trip Accountability Form
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowEvalForm(true)} data-testid="action-eval">
                    <ClipboardList className="w-4 h-4 mr-2 text-primary-600" />
                    End of Season Evaluation
                    {evalSubmitted && <Badge className="ml-auto text-[10px] bg-green-100 text-green-700 border-0 px-1.5">Done</Badge>}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowMessages(true)} data-testid="action-messages">
                    <MessageSquare className="w-4 h-4 mr-2 text-green-600" />
                    Messages
                    {unreadMessages > 0 && <Badge className="ml-auto text-[10px] bg-red-500 text-white border-0 px-1.5">{unreadMessages}</Badge>}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <div className="min-w-0 hidden sm:block">
              <h1 className="font-heading font-bold text-primary-600 text-sm sm:text-lg truncate">
                {user?.group_name || "All Groups"}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-xs font-medium text-primary-600 sm:hidden">{user?.group_name}</span>
            <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200 hidden sm:flex">
              <RefreshCw className="w-3 h-3 mr-1" />
              {formatLastSync(syncStatus?.last_sync)}
            </Badge>
            <Button variant="ghost" size="sm" onClick={logout} className="text-muted-foreground" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="px-4 sm:px-6 pb-3 bg-white">
          <div className="max-w-7xl mx-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search campers by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10 sm:h-11 bg-muted/50"
              data-testid="camper-search-input"
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-4 sm:p-5 md:p-6">
        <div className="mb-3 sm:mb-4 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {filteredCampers.length} camper{filteredCampers.length !== 1 ? 's' : ''} found
          </p>
        </div>

        {filteredCampers.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="font-heading text-xl text-muted-foreground mb-2">
              No Campers Found
            </h3>
            <p className="text-sm text-muted-foreground">
              {searchQuery ? "Try a different search term" : "Campers will appear here after sync"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
            {filteredCampers.map((camper) => (
              <CamperCard 
                key={camper.id} 
                camper={camper} 
                hasAlerts={hasMedicalAlerts(camper)}
                onClick={() => setSelectedCamper(camper)}
                isNursery={(user?.group_code || "").toUpperCase().startsWith("N")}
              />
            ))}
          </div>
        )}
      </main>

      {/* Camper Detail Modal */}
      <Dialog open={!!selectedCamper} onOpenChange={() => setSelectedCamper(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] p-0 w-[calc(100%-2rem)] sm:w-full rounded-xl" aria-describedby="camper-detail-description">
          <span id="camper-detail-description" className="sr-only">Camper information and parent details</span>
          {selectedCamper && (
            <CamperDetail 
              camper={selectedCamper} 
              customFields={customFields}
              onClose={() => setSelectedCamper(null)}
              readOnly={!isCurrentYear}
              userGroupCode={user?.group_code}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* End of Season Program Evaluation Dialog */}
      <Dialog open={showEvalForm} onOpenChange={setShowEvalForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[calc(100%-2rem)] sm:w-full rounded-xl" aria-describedby="eval-form-description">
          <span id="eval-form-description" className="sr-only">End of Season Program Evaluation Form</span>
          <DialogHeader>
            <DialogTitle className="font-heading text-lg sm:text-xl flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-primary-600" />
              End of Season Program Evaluation
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              {user?.group_name} ({user?.group_code}) — {evalSubmitted ? "Previously submitted (you can update)" : "Please complete all sections"}
            </p>
          </DialogHeader>
          
          <div className="space-y-5 py-2">
            <div>
              <Label htmlFor="eval-name" className="text-sm font-semibold">Head Counselor Name *</Label>
              <Input
                id="eval-name"
                placeholder="Your full name"
                value={evalForm.head_counselor}
                onChange={(e) => setEvalForm(f => ({...f, head_counselor: e.target.value}))}
                className="mt-1"
                data-testid="eval-name-input"
              />
            </div>

            <div>
              <Label htmlFor="eval-q1" className="text-sm font-semibold">
                1. Were the activities your group did age appropriate? Too hard? Too easy? Any specific ones that didn't work?
              </Label>
              <Textarea
                id="eval-q1"
                placeholder="Share your thoughts on activity appropriateness..."
                value={evalForm.activity_appropriateness}
                onChange={(e) => setEvalForm(f => ({...f, activity_appropriateness: e.target.value}))}
                className="mt-1 min-h-[100px]"
                data-testid="eval-q1-input"
              />
            </div>

            <div>
              <Label htmlFor="eval-q2" className="text-sm font-semibold">
                2. Would you have liked more or fewer of any activities? Please include explanations.
              </Label>
              <Textarea
                id="eval-q2"
                placeholder="Share your thoughts on activity frequency..."
                value={evalForm.activity_frequency}
                onChange={(e) => setEvalForm(f => ({...f, activity_frequency: e.target.value}))}
                className="mt-1 min-h-[100px]"
                data-testid="eval-q2-input"
              />
            </div>

            <div>
              <Label htmlFor="eval-q3" className="text-sm font-semibold">
                3. How can we improve group programming? What changes would you make to the day-to-day activities or schedule?
              </Label>
              <Textarea
                id="eval-q3"
                placeholder="Suggestions for programming improvement..."
                value={evalForm.programming_improvement}
                onChange={(e) => setEvalForm(f => ({...f, programming_improvement: e.target.value}))}
                className="mt-1 min-h-[100px]"
                data-testid="eval-q3-input"
              />
            </div>

            <div>
              <Label htmlFor="eval-q4" className="text-sm font-semibold">
                4. Any other ideas, issues, or complaints? Please include explanations.
              </Label>
              <Textarea
                id="eval-q4"
                placeholder="Any other feedback..."
                value={evalForm.other_ideas}
                onChange={(e) => setEvalForm(f => ({...f, other_ideas: e.target.value}))}
                className="mt-1 min-h-[100px]"
                data-testid="eval-q4-input"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowEvalForm(false)} data-testid="eval-cancel-btn">
                Cancel
              </Button>
              <Button 
                onClick={handleEvalSubmit} 
                disabled={evalSubmitting}
                data-testid="eval-submit-btn"
              >
                {evalSubmitting ? "Submitting..." : evalSubmitted ? "Update Evaluation" : "Submit Evaluation"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}

const SESSION_SHORT_LABELS = {
  "Full Season- 5 Days": "FS5",
  "Half Season 1- 5 Days": "HS1",
  "Half Season 2- 5 Days": "HS2",
  "6 Week Season": "6W",
  "Flex 20 Days": "F20",
  "Flex 25 Days": "F25",
  "Flex 30 Days": "F30",
};

function CamperCard({ camper, hasAlerts, onClick, isNursery }) {
  const initials = `${camper.first_name?.[0] || ''}${camper.last_name?.[0] || ''}`.toUpperCase();
  const displayName = camper.first_name;
  const sessionTags = (camper.sessions || []).map(s => SESSION_SHORT_LABELS[s.name] || s.name).filter(Boolean);
  const birthday = camper.date_of_birth ? new Date(camper.date_of_birth + 'T00:00:00').toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric' }) : null;
  
  return (
    <div
      onClick={onClick}
      className="group relative overflow-hidden rounded-xl border bg-white shadow-sm hover:shadow-lg transition-all duration-200 hover:-translate-y-1 cursor-pointer active:scale-[0.98]"
      data-testid={`camper-card-${camper.id}`}
    >
      {hasAlerts && (
        <div className="absolute top-0 right-0 w-0 h-0 border-t-[36px] border-t-red-500 border-l-[36px] border-l-transparent">
          <AlertTriangle className="absolute -top-8 right-0.5 w-3.5 h-3.5 text-white" />
        </div>
      )}
      
      <div className="p-4 sm:p-5">
        <div className="flex items-start gap-3 sm:gap-4">
          <CamperPhoto
            camperId={camper.id}
            firstName={camper.first_name}
            lastName={camper.last_name}
            size={56}
            className="flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-semibold text-foreground text-sm sm:text-base truncate">
              {displayName} {camper.last_name}
            </h3>
            <div className="flex flex-wrap items-center gap-1.5 mt-1.5 sm:mt-2">
              {camper.group_code && (
                <Badge variant="secondary" className="text-xs bg-primary-50 text-primary-700 border-primary-200">
                  {camper.cit_subgroup || camper.group_code}
                </Badge>
              )}
              {camper.age && (
                <span className="text-xs text-muted-foreground">
                  Age {Math.floor(camper.age)}
                </span>
              )}
              {isNursery ? (
                <Badge variant="outline" className="text-xs border-0 px-1.5" data-testid="swim-color-badge">
                  <span className="w-2.5 h-2.5 rounded-full inline-block mr-1 bg-green-500" />
                  Kiddie
                </Badge>
              ) : camper.swim_color ? (
                <Badge variant="outline" className="text-xs border-0 px-1.5" data-testid="swim-color-badge">
                  <span className={`w-2.5 h-2.5 rounded-full inline-block mr-1 ${
                    (() => {
                      const c = (camper.swim_color || "").toLowerCase();
                      if (c.includes("red") && c.includes("blue")) return "bg-gradient-to-r from-red-500 to-blue-500";
                      if (c.includes("yellow") && c.includes("blue")) return "bg-gradient-to-r from-yellow-400 to-blue-500";
                      if (c.includes("green") && c.includes("blue")) return "bg-gradient-to-r from-green-500 to-blue-500";
                      if (c === "red") return "bg-red-500";
                      if (c === "yellow") return "bg-yellow-400";
                      if (c === "blue") return "bg-blue-500";
                      if (c === "green") return "bg-green-500";
                      return "bg-gray-300";
                    })()
                  }`} />
                  {camper.swim_color}
                </Badge>
              ) : null}
              {camper.years_at_camp != null && (
                <Badge variant="outline" className="text-xs border-indigo-300 text-indigo-600 bg-indigo-50" data-testid="years-at-camp">
                  Yr {camper.years_at_camp}
                </Badge>
              )}
              {camper.previous_group && (
                <Badge variant="outline" className="text-xs border-slate-300 text-slate-600 bg-slate-50" data-testid="prev-group">
                  Prev: {camper.previous_group}
                </Badge>
              )}
              {sessionTags.map(tag => (
                <Badge key={tag} variant="outline" className={`text-xs ${
                  tag.startsWith('F') && tag !== 'FS5' ? 'border-orange-300 text-orange-600 bg-orange-50' : 'border-emerald-300 text-emerald-700 bg-emerald-50'
                }`} data-testid={`session-tag-${tag.toLowerCase()}`}>
                  {tag}
                </Badge>
              ))}
            </div>
            {birthday && (
              <p className="text-xs text-muted-foreground mt-1" data-testid="camper-birthday">
                Born {birthday}
              </p>
            )}
            {camper.lunch_order && camper.lunch_date === new Date().toISOString().split('T')[0] && (
              <div className="flex items-center gap-1.5 mt-1.5 px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded-lg" data-testid="camper-lunch">
                <Utensils className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                <span className="text-xs font-medium text-amber-800 truncate">{camper.lunch_order}</span>
              </div>
            )}
            {(camper.am_bus || camper.pm_bus || camper.am_method || camper.pm_method || camper.pm_weekly_today) && (
              <div className="flex flex-col gap-1 mt-1.5 px-2.5 py-1.5 bg-green-50 border border-green-200 rounded-lg" data-testid="camper-bus">
                <div className="flex items-center gap-2">
                  <Bus className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                  <span className="text-xs font-medium text-green-800">
                    {camper.am_bus && <>AM: {camper.am_bus}</>}
                    {camper.am_bus && (camper.pm_bus || camper.pm_weekly_today) && <span className="mx-1 text-green-400">|</span>}
                    {camper.pm_weekly_today ? (
                      <>
                        PM: {camper.pm_weekly_location}{camper.pm_weekly_remove ? " (not on pickup list)" : ""}
                        <span className="ml-1 text-[9px] font-bold uppercase px-1 py-px rounded bg-emerald-200 text-emerald-800 border border-emerald-300 align-middle" data-testid={`camper-pm-weekly-${camper.id}`}>Weekly</span>
                      </>
                    ) : camper.pm_bus ? <>PM: {camper.pm_bus}</> : null}
                  </span>
                </div>
                {(camper.am_method || camper.pm_method) && (
                  <span className="text-[10px] text-green-600 ml-5">
                    {camper.am_method && <>Drop-off: {camper.am_method}</>}
                    {camper.am_method && camper.pm_method && <> · </>}
                    {camper.pm_method && <>Pickup: {camper.pm_method}</>}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        
        {hasAlerts && (
          <div className="mt-2.5 sm:mt-3 pt-2.5 sm:pt-3 border-t">
            <Badge className="text-xs bg-red-50 text-red-700 border-red-200">
              <AlertTriangle className="w-3 h-3 mr-1" />
              Medical Alert
            </Badge>
          </div>
        )}
      </div>
      
      <div className="px-4 sm:px-5 py-2.5 sm:py-3 bg-muted/30 border-t text-xs text-muted-foreground flex items-center justify-between">
        <span>View Details</span>
        <span className="opacity-0 group-hover:opacity-100 transition-opacity">&rarr;</span>
      </div>
    </div>
  );
}

function RosterCamperCard({ camper, hasAlerts, camperAlerts = [], onClick, isAssistant = false, isNursery, rosterDate, isOvernightDay = false, isBroadwayDay = false }) {
  const initials = `${camper.first_name?.[0] || ''}${camper.last_name?.[0] || ''}`.toUpperCase();
  const displayName = camper.first_name;
  const hasLunch = camper.lunch_order && camper.lunch_date === rosterDate;
  const sessionTags = (camper.sessions || []).map(s => SESSION_SHORT_LABELS[s.name] || s.name).filter(Boolean);

  // Swim lesson for this day of week
  const swimLesson = (() => {
    if (!rosterDate || !camper.swim_lessons) return null;
    try {
      const d = new Date(rosterDate + "T12:00:00");
      const dayName = d.toLocaleDateString("en-US", { weekday: "long" });
      if (dayName === "Saturday") return null;
      return camper.swim_lessons[dayName] || null;
    } catch { return null; }
  })();

  const swimColorClass = (() => {
    if (isNursery) return "bg-green-500";
    const c = (camper.swim_color || "").toLowerCase();
    if (c.includes("red") && c.includes("blue")) return "bg-gradient-to-r from-red-500 to-blue-500";
    if (c.includes("yellow") && c.includes("blue")) return "bg-gradient-to-r from-yellow-400 to-blue-500";
    if (c.includes("green") && c.includes("blue")) return "bg-gradient-to-r from-green-500 to-blue-500";
    if (c === "red") return "bg-red-500";
    if (c === "yellow") return "bg-yellow-400";
    if (c === "blue") return "bg-blue-500";
    if (c === "green") return "bg-green-500";
    return "bg-gray-300";
  })();
  const swimLabel = isNursery ? "Kiddie Pool" : camper.swim_color || "Not Set";

  return (
    <div
      onClick={isAssistant ? undefined : onClick}
      className={`group relative overflow-hidden rounded-xl border bg-white shadow-sm transition-all duration-200 ${isAssistant ? "cursor-default" : "hover:shadow-lg hover:-translate-y-1 cursor-pointer active:scale-[0.98]"}`}
      data-testid={`roster-camper-card-${camper.id}`}
    >
      {hasAlerts && (
        <div className="absolute top-0 right-0 w-0 h-0 border-t-[36px] border-t-red-500 border-l-[36px] border-l-transparent">
          <AlertTriangle className="absolute -top-8 right-0.5 w-3.5 h-3.5 text-white" />
        </div>
      )}
      <div className="p-4">
        <div className="flex items-start gap-3">
          <CamperPhoto
            camperId={camper.id}
            firstName={camper.first_name}
            lastName={camper.last_name}
            size={44}
            className="flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-semibold text-foreground text-sm truncate">{displayName} {camper.last_name}</h3>
            <div className="flex flex-wrap items-center gap-1 mt-0.5">
              {isOvernightDay && camper.overnight_trip && (
                <Badge className="text-[10px] px-1.5 py-0 bg-gradient-to-r from-indigo-600 to-violet-700 text-white border-0" data-testid={`badge-overnight-${camper.id}`}>
                  <Moon className="w-2.5 h-2.5 mr-0.5" />Overnight
                </Badge>
              )}
              {isBroadwayDay && camper.broadway_trip && (
                <Badge className="text-[10px] px-1.5 py-0 bg-gradient-to-r from-pink-500 to-rose-600 text-white border-0" data-testid={`badge-broadway-${camper.id}`}>
                  <Drama className="w-2.5 h-2.5 mr-0.5" />Broadway
                </Badge>
              )}
              {sessionTags.map(tag => (
                <Badge key={tag} variant="outline" className={`text-[10px] px-1.5 py-0 ${tag.startsWith('F') && !tag.startsWith('FS') ? 'border-orange-300 text-orange-600 bg-orange-50' : 'border-emerald-300 text-emerald-700 bg-emerald-50'}`}>
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        {/* Lunch Order */}
        <div className="mt-3 flex items-center gap-2 px-2.5 py-2 rounded-lg border bg-amber-50 border-amber-200" data-testid={`roster-lunch-${camper.id}`}>
          <Utensils className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
          {hasLunch ? (
            <span className="text-xs font-medium text-amber-800 truncate">{camper.lunch_order}</span>
          ) : (
            <span className="text-xs text-amber-400 italic">No lunch order</span>
          )}
        </div>

        {/* Late / Early Dismissal banner — fires when the Attendance App marks
            the kid late or dismissed today. Stays on the card all day so it's
            never just a dismissible dashboard banner the counselor can miss. */}
        {(camper.late_arrival_today || camper.early_dismissed_today) && (
          <div className="mt-2 flex items-start gap-2 px-2.5 py-2 rounded-lg border bg-amber-50 border-amber-300" data-testid={`roster-attendance-event-${camper.id}`}>
            <AlertCircle className="w-3.5 h-3.5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              {camper.late_arrival_today && (
                <span className="text-xs font-bold text-amber-800 block" data-testid={`roster-late-${camper.id}`}>
                  ARRIVED LATE TODAY
                </span>
              )}
              {camper.early_dismissed_today && (
                <span className="text-xs font-bold text-amber-800 block" data-testid={`roster-dismissed-${camper.id}`}>
                  DISMISSED EARLY{camper.dismissal_time ? ` AT ${camper.dismissal_time}` : ""}{camper.pickup_by ? ` — picked up by ${camper.pickup_by}` : ""}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Bus Info — shows AM/PM transport for the day. AM/PM dismissal exceptions
            (e.g. "Car Drop Off", "After Care") OVERRIDE the bus assignment for today
            and render in red so counselors instantly see today's plan. */}
        <div className={`mt-2 px-2.5 py-2 rounded-lg border ${(camper.am_exception_today || camper.pm_exception_today) ? "bg-rose-50 border-rose-300" : "bg-green-50 border-green-200"}`} data-testid={`roster-bus-${camper.id}`}>
          <div className="flex items-start gap-2">
            <Bus className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${(camper.am_exception_today || camper.pm_exception_today) ? "text-rose-600" : "text-green-600"}`} />
            <div className="flex-1 min-w-0">
              {(camper.am_exception_today || camper.pm_exception_today) ? (
                <div className="flex flex-col gap-0.5">
                  {camper.am_exception_today ? (
                    <span className="text-xs font-bold text-rose-700" data-testid={`roster-am-exception-${camper.id}`}>
                      AM TODAY: {camper.am_exception_location}
                    </span>
                  ) : camper.am_bus ? (
                    <span className="text-xs font-medium text-green-800">AM: {camper.am_bus}</span>
                  ) : null}
                  {camper.pm_exception_today ? (
                    <span className="text-xs font-bold text-rose-700" data-testid={`roster-pm-exception-${camper.id}`}>
                      PM TODAY: {camper.pm_exception_location}
                    </span>
                  ) : camper.pm_weekly_today ? (
                    <span className="text-xs font-medium text-green-800" data-testid={`roster-pm-weekly-${camper.id}`}>
                      PM: {camper.pm_weekly_location}{camper.pm_weekly_remove ? " (not on pickup list)" : ""}
                      <span className="ml-1 text-[9px] font-bold uppercase px-1 py-px rounded bg-emerald-200 text-emerald-800 border border-emerald-300 align-middle">Weekly</span>
                    </span>
                  ) : camper.pm_bus ? (
                    <span className="text-xs font-medium text-green-800">PM: {camper.pm_bus}</span>
                  ) : null}
                </div>
              ) : (camper.am_bus || camper.pm_bus || camper.pm_weekly_today) ? (
                <span className="text-xs font-medium text-green-800">
                  {camper.am_bus && <>AM: {camper.am_bus}</>}
                  {camper.am_bus && (camper.pm_bus || camper.pm_weekly_today) && <span className="mx-1 text-green-400">|</span>}
                  {camper.pm_weekly_today ? (
                    <>
                      PM: {camper.pm_weekly_location}{camper.pm_weekly_remove ? " (not on pickup list)" : ""}
                      <span className="ml-1 text-[9px] font-bold uppercase px-1 py-px rounded bg-emerald-200 text-emerald-800 border border-emerald-300 align-middle" data-testid={`roster-pm-weekly-tag-${camper.id}`}>Weekly</span>
                    </>
                  ) : camper.pm_bus ? <>PM: {camper.pm_bus}</> : null}
                </span>
              ) : (
                <span className="text-xs text-green-400 italic">No transport assigned</span>
              )}
            </div>
          </div>
        </div>

        {/* Services for today — speech / OT / tutoring etc.
            Source: backend `/attendance/roster/{group}` enriches each row
            with `services: [{service, provider, location, time}]` filtered
            to the day of week. Counselor needs to release the kid at the
            right time to the right place — show it ON the card. */}
        {Array.isArray(camper.services) && camper.services.length > 0 && (
          <div className="mt-2 px-2.5 py-2 rounded-lg border bg-violet-50 border-violet-200"
               data-testid={`roster-services-${camper.id}`}>
            <div className="flex items-start gap-2">
              <Stethoscope className="w-3.5 h-3.5 text-violet-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0 space-y-1">
                {camper.services.map((s, i) => (
                  <div key={i} className="text-xs"
                       data-testid={`roster-service-${camper.id}-${i}`}>
                    <span className="font-semibold text-violet-800">{s.service}</span>
                    {s.location && (
                      <span className="text-violet-700"> · {s.location}</span>
                    )}
                    {s.time && (
                      <span className="text-violet-700"> · {s.time}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Swim Color */}
        <div className="mt-2 flex items-center gap-2 px-2.5 py-2 rounded-lg border bg-sky-50 border-sky-200" data-testid={`roster-swim-${camper.id}`}>
          <Waves className="w-3.5 h-3.5 text-sky-600 flex-shrink-0" />
          <span className={`w-3 h-3 rounded-full inline-block flex-shrink-0 ${swimColorClass}`} />
          <span className="text-xs font-medium text-sky-800">{swimLabel}</span>
        </div>

        {/* Swim Lesson */}
        {swimLesson && (
          <div className="mt-2 flex items-center gap-2 px-2.5 py-2 rounded-lg border bg-cyan-50 border-cyan-200" data-testid={`roster-swim-lesson-${camper.id}`}>
            <Droplets className="w-3.5 h-3.5 text-cyan-600 flex-shrink-0" />
            <span className="text-xs font-medium text-cyan-800">Swim Lesson: {swimLesson}</span>
          </div>
        )}

        {/* Color War */}
        {camper.color_war && (
          <div className={`mt-2 flex items-center gap-2 px-2.5 py-2 rounded-lg border ${camper.color_war === "blue" ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-300"}`} data-testid={`roster-color-war-${camper.id}`}>
            <Flag className={`w-3.5 h-3.5 flex-shrink-0 ${camper.color_war === "blue" ? "text-blue-600" : "text-gray-600"}`} />
            <span className={`text-xs font-bold uppercase ${camper.color_war === "blue" ? "text-blue-700" : "text-gray-700"}`}>{camper.color_war}</span>
          </div>
        )}

        {/* Early Dismissal / Late Arrival alerts for this camper */}
        {camperAlerts.map((a) => (
          <div
            key={a.id}
            className={`mt-2 flex items-center gap-2 px-2.5 py-2 rounded-lg border ${a.type === "early_dismissal" ? "bg-orange-50 border-orange-300" : "bg-sky-50 border-sky-300"}`}
            data-testid={`roster-dismissal-${camper.id}-${a.id}`}
          >
            <Bell className={`w-3.5 h-3.5 flex-shrink-0 ${a.type === "early_dismissal" ? "text-orange-600" : "text-sky-600"}`} />
            <span className={`text-xs font-bold ${a.type === "early_dismissal" ? "text-orange-800" : "text-sky-800"}`}>
              {a.type === "early_dismissal" ? "Early Dismissal" : "Late Arrival"}: {a.pickup_time || a.dismissal_time || ""}
            </span>
          </div>
        ))}

        <CounselorCITCardInline camper={camper} />
      </div>

      {!isAssistant && (
        <div className="px-4 py-2 bg-muted/30 border-t text-xs text-muted-foreground flex items-center justify-between">
          <span>View Details</span>
          <span className="opacity-0 group-hover:opacity-100 transition-opacity">&rarr;</span>
        </div>
      )}
    </div>
  );
}


const COUNSELOR_CIT_TRIP_DATES_BY_NUM = {
  1: "2026-07-01",
  2: "2026-07-07",
  3: "2026-07-13",
  4: "2026-07-23",
  5: "2026-07-27",
  6: "2026-08-04",
  7: "2026-08-11",
  8: "2026-08-17",
};

function formatCounselorCitTripDate(tripName) {
  const m = /^Trip\s+(\d+)/i.exec(tripName || "");
  if (!m) return "";
  const iso = COUNSELOR_CIT_TRIP_DATES_BY_NUM[parseInt(m[1], 10)];
  if (!iso) return "";
  try {
    return new Date(iso + "T12:00:00").toLocaleDateString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

function CounselorCITCardInline({ camper }) {
  const [open, setOpen] = useState(false);
  const gc = (camper.group_code || "").toUpperCase();
  if (!gc.startsWith("CIT")) return null;

  const ranks = [1, 2, 3];
  const choicePairs = ranks
    .map(r => ({
      rank: r,
      program: camper[`cit_program_${r}`] || "",
      choice: camper[`cit_choice_${r}`] || "",
    }))
    .filter(p => p.program || p.choice);

  const trips = camper.cit_trips || {};
  const enrolledTrips = Object.entries(trips).filter(([, v]) => v && String(v).trim().length > 0);
  const paperwork = camper.cit_parent_signature || "";

  const hasAny = choicePairs.length > 0 || enrolledTrips.length > 0 || paperwork;
  if (!hasAny) return null;

  return (
    <div className="mt-2" data-testid={`counselor-cit-card-inline-${camper.id}`}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        className="w-full flex items-center justify-between gap-1.5 px-2.5 py-1.5 rounded-md border border-fuchsia-200 bg-fuchsia-50 hover:bg-fuchsia-100 transition-colors"
        data-testid={`counselor-cit-card-inline-toggle-${camper.id}`}
      >
        <span className="flex items-center gap-1.5 text-xs font-semibold text-fuchsia-800">
          <Star className="w-3 h-3" />
          CIT Info
          {paperwork ? (
            <Badge variant="outline" className="h-4 px-1 text-[9px] border-emerald-300 text-emerald-700 bg-white">&#10003; Paper</Badge>
          ) : (
            <Badge variant="outline" className="h-4 px-1 text-[9px] border-amber-300 text-amber-700 bg-white">No Paper</Badge>
          )}
          {enrolledTrips.length > 0 && (
            <Badge variant="outline" className="h-4 px-1 text-[9px] border-fuchsia-300 text-fuchsia-700 bg-white">{enrolledTrips.length} trips</Badge>
          )}
        </span>
        {open ? <ChevronUp className="w-3 h-3 text-fuchsia-700" /> : <ChevronDown className="w-3 h-3 text-fuchsia-700" />}
      </button>
      {open && (
        <div
          className="mt-1.5 p-2 rounded-md border border-fuchsia-100 bg-white space-y-1.5"
          onClick={(e) => e.stopPropagation()}
        >
          {choicePairs.length > 0 && (
            <div className="space-y-0.5">
              <p className="text-[9px] font-semibold uppercase tracking-wider text-fuchsia-700">Choices</p>
              {choicePairs.map(p => (
                <div key={p.rank} className="flex items-start gap-1.5 text-[11px] leading-tight">
                  <span className="font-bold text-fuchsia-700">#{p.rank}</span>
                  <span className="text-slate-700">
                    {p.program && <span className="font-medium">{p.program}</span>}
                    {p.program && p.choice && <span className="text-slate-400"> · </span>}
                    {p.choice}
                  </span>
                </div>
              ))}
            </div>
          )}
          {enrolledTrips.length > 0 && (
            <div className="space-y-0.5">
              <p className="text-[9px] font-semibold uppercase tracking-wider text-fuchsia-700">Trips</p>
              <div className="flex flex-wrap gap-1">
                {enrolledTrips.map(([name]) => {
                  const d = formatCounselorCitTripDate(name);
                  return (
                    <Badge key={name} variant="outline" className="h-4 px-1 text-[9px] border-fuchsia-300 text-fuchsia-700 bg-fuchsia-50">
                      {name}{d ? ` · ${d}` : ""}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}
          {paperwork && (
            <p className="text-[10px] text-slate-600"><span className="font-semibold">Paperwork:</span> {paperwork}</p>
          )}
        </div>
      )}
    </div>
  );
}


function DismissalExceptionEditor({ camperId, camperName }) {
  const [exc, setExc] = useState(null);
  const [location, setLocation] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const today = useMemo(() => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" }), []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/admin/dismissal-exceptions/${camperId}`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { date: today },
        });
        if (res.data && res.data.location) {
          setExc(res.data);
          setLocation(res.data.location);
          setNote(res.data.note || "");
        } else {
          setExc(null);
          setLocation("");
          setNote("");
        }
      } catch {
        setExc(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [camperId, today]);

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.put(
        `${API}/admin/dismissal-exceptions/${camperId}`,
        { location: location || null, note, date: today },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.status === "cleared") {
        setExc(null);
        toast.success("Exception cleared");
      } else {
        setExc(res.data);
        toast.success(`Today's dismissal set to ${location}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <section data-testid="dismissal-exception-editor" className="border-2 border-dashed border-rose-200 rounded-xl p-3 sm:p-4 bg-rose-50/40">
      <h3 className="font-heading font-semibold text-base flex items-center gap-2 mb-1 text-rose-800">
        <Bus className="w-4 h-4" />
        Today's Dismissal Exception
      </h3>
      <p className="text-xs text-muted-foreground mb-2">
        Override {camperName.split(" ")[0]}'s dismissal for <strong>{today}</strong> only. Auto-resets tomorrow.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mb-2">
        {["Main Tent","Hockey Rink","Auditorium","After Care","Front Door","Classroom","Overnight","Broadway"].map(opt => (
          <button
            key={opt}
            type="button"
            onClick={() => setLocation(opt === location ? "" : opt)}
            className={`px-2 py-1.5 text-xs font-semibold rounded-lg border transition ${location === opt ? "bg-rose-600 text-white border-rose-700" : "bg-white text-rose-800 border-rose-200 hover:bg-rose-100"}`}
            data-testid={`dismissal-exc-${opt.replace(/\s+/g, '-').toLowerCase()}`}
          >
            {opt}
          </button>
        ))}
      </div>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Note (optional) — e.g., parent calling to pick up at 2:45"
        className="w-full min-h-[44px] p-2 text-sm border rounded-lg bg-white"
        data-testid="dismissal-exc-note"
      />
      <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
        <div className="text-xs text-muted-foreground">
          {exc && exc.location ? <span><strong>Active:</strong> {exc.location}</span> : <span>No exception today.</span>}
        </div>
        <div className="flex gap-2">
          {exc && (
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              onClick={() => { setLocation(""); setNote(""); save(); }}
              disabled={saving}
              data-testid="dismissal-exc-clear"
            >
              Clear
            </Button>
          )}
          <Button
            size="sm"
            className="bg-rose-600 hover:bg-rose-700 text-white h-8 text-xs"
            onClick={save}
            disabled={saving || (!location && !exc)}
            data-testid="dismissal-exc-save"
          >
            <Save className="w-3 h-3 mr-1" />
            {saving ? "Saving…" : "Save Exception"}
          </Button>
        </div>
      </div>
    </section>
  );
}


function CamperDetail({ camper, customFields, onClose, readOnly, userGroupCode }) {
  const initials = `${camper.first_name?.[0] || ''}${camper.last_name?.[0] || ''}`.toUpperCase();
  const medicalData = camper.medical_data || {};
  const contacts = camper.contact_details || {};
  const parentInfo = camper.parent_info || {};
  
  const handleDownloadPDF = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/campers/${camper.id}/pdf`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Failed to generate PDF");
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${camper.last_name}_${camper.first_name}_snapshot.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("PDF downloaded!");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };
  
  return (
    <>
      <DialogHeader className="p-4 sm:p-6 pb-0">
        <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
          <div className="flex items-start gap-3 sm:gap-4 w-full sm:w-auto">
            <CamperPhoto
              camperId={camper.id}
              firstName={camper.first_name}
              lastName={camper.last_name}
              size={80}
              className="flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <DialogTitle className="font-heading text-xl sm:text-2xl text-primary-700">
                {camper.first_name} {camper.last_name}
              </DialogTitle>
              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-1.5 sm:mt-2">
                {camper.group_code && (
                  <Badge variant="secondary" className="bg-primary-50 text-primary-700 text-xs">
                    <Users className="w-3 h-3 mr-1" />
                    {camper.cit_subgroup || camper.group_name || camper.group_code}
                  </Badge>
                )}
                {camper.age && (
                  <Badge variant="outline" className="text-xs">
                    <Calendar className="w-3 h-3 mr-1" />
                    Age {Math.floor(camper.age)}
                  </Badge>
                )}
                {camper.gender && (
                  <Badge variant="outline" className="text-xs">
                    <User className="w-3 h-3 mr-1" />
                    {camper.gender}
                  </Badge>
                )}
                {camper.date_of_birth && (
                  <Badge variant="outline" className="text-xs">
                    <Calendar className="w-3 h-3 mr-1" />
                    {new Date(camper.date_of_birth + 'T00:00:00').toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric' })}
                  </Badge>
                )}
                {camper.years_at_camp != null && (
                  <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">
                    {camper.years_at_camp} {camper.years_at_camp === 1 ? 'Year' : 'Years'} at Camp
                  </Badge>
                )}
                {camper.previous_group && (
                  <Badge variant="outline" className="text-xs bg-slate-50 text-slate-700 border-slate-200">
                    Prev Group: {camper.previous_group}
                  </Badge>
                )}
                {camper.color_war && (
                  <Badge className={`text-xs font-bold uppercase ${camper.color_war === "blue" ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-800 border border-gray-400"}`}>
                    <Flag className="w-3 h-3 mr-1" />
                    {camper.color_war}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleDownloadPDF}
            className="flex-shrink-0 w-full sm:w-auto"
            data-testid="download-pdf-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Download PDF
          </Button>
        </div>
      </DialogHeader>

      <ScrollArea className="max-h-[55vh] sm:max-h-[60vh]">
        <div className="p-4 sm:p-6 space-y-5 sm:space-y-6">
          {/* Parent/Guardian Information */}
          {(parentInfo.name || parentInfo.phone || parentInfo.email) && (
            <section data-testid="parent-info-section">
              <h3 className="font-heading font-semibold text-base sm:text-lg flex items-center gap-2 mb-3 sm:mb-4 text-primary-700">
                <User className="w-4 h-4 sm:w-5 sm:h-5" />
                Parent / Guardian
              </h3>
              <div className="grid gap-2 sm:gap-3">
                {parentInfo.name && (
                  <div className="flex items-center gap-3 p-2.5 sm:p-3 bg-blue-50 rounded-lg border border-blue-100" data-testid="parent-name">
                    <User className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">Name</p>
                      <p className="text-sm font-medium truncate">{parentInfo.name}</p>
                    </div>
                  </div>
                )}
                {parentInfo.phone && (
                  <div className="flex items-center gap-3 p-2.5 sm:p-3 bg-blue-50 rounded-lg border border-blue-100" data-testid="parent-phone">
                    <Phone className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">Phone</p>
                      <p className="text-sm font-medium">{formatPhone(parentInfo.phone)}</p>
                    </div>
                  </div>
                )}
                {parentInfo.email && (
                  <div className="flex items-center gap-3 p-2.5 sm:p-3 bg-blue-50 rounded-lg border border-blue-100" data-testid="parent-email">
                    <Mail className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">Email</p>
                      <p className="text-sm font-medium break-all">{parentInfo.email}</p>
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Program / Enrolled Session */}
          {camper.sessions && camper.sessions.length > 0 && (
            <section data-testid="program-section">
              <h3 className="font-heading font-semibold text-base sm:text-lg flex items-center gap-2 mb-3 text-primary-700">
                <CalendarDays className="w-4 h-4 sm:w-5 sm:h-5" />
                Program
              </h3>
              <div className="grid gap-2">
                {camper.sessions.map((s, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                    <span className="text-sm font-medium text-emerald-800">{s.name}</span>
                    <span className="text-xs text-emerald-600">
                      {new Date(s.start + 'T00:00:00').toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' })} – {new Date(s.end + 'T00:00:00').toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* AM Drop-off / PM Pickup */}
          {(camper.am_bus || camper.pm_bus || camper.am_method || camper.pm_method || camper.pm_weekly_today) && (
            <section data-testid="transport-section">
              <h3 className="font-heading font-semibold text-base sm:text-lg flex items-center gap-2 mb-3 text-primary-700">
                <Bus className="w-4 h-4 sm:w-5 sm:h-5" />
                Transportation
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {(camper.am_bus || camper.am_method) && (
                  <div className="p-3 bg-sky-50 rounded-lg border border-sky-100" data-testid="am-transport">
                    <p className="text-xs text-muted-foreground">AM Drop-off</p>
                    {camper.am_bus && (
                      <p className="text-sm font-bold text-sky-800">{camper.am_bus}</p>
                    )}
                    {camper.am_method && camper.am_method !== camper.am_bus && (
                      <p className={`text-xs ${camper.am_bus ? "text-sky-600 mt-0.5" : "text-sm font-bold text-sky-800"}`}>
                        {camper.am_method}
                      </p>
                    )}
                  </div>
                )}
                {(camper.pm_bus || camper.pm_method || camper.pm_weekly_today) && (
                  <div className="p-3 bg-orange-50 rounded-lg border border-orange-100" data-testid="pm-transport">
                    <p className="text-xs text-muted-foreground">PM Pickup</p>
                    {camper.pm_weekly_today ? (
                      <>
                        <p className="text-sm font-bold text-orange-800">{camper.pm_weekly_location}</p>
                        {camper.pm_weekly_remove && (<p className="text-xs text-orange-600 mt-0.5">Not on a pickup list today</p>)}
                        <span className="inline-block mt-1 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-200 text-emerald-800 border border-emerald-300">Weekly schedule</span>
                      </>
                    ) : (
                      <>
                        {camper.pm_bus && (
                          <p className="text-sm font-bold text-orange-800">{camper.pm_bus}</p>
                        )}
                        {camper.pm_method && camper.pm_method !== camper.pm_bus && (
                          <p className={`text-xs ${camper.pm_bus ? "text-orange-600 mt-0.5" : "text-sm font-bold text-orange-800"}`}>
                            {camper.pm_method}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Authorized Pickup + Travel/Additional Info (CampMinder custom fields) */}
          {(camper.authorized_pickup || camper.travel_additional_info) && (
            <section data-testid="pickup-info-section">
              <h3 className="font-heading font-semibold text-base sm:text-lg flex items-center gap-2 mb-3 text-primary-700">
                <User className="w-4 h-4 sm:w-5 sm:h-5" />
                Pickup & Travel
              </h3>
              <div className="grid gap-2">
                {camper.authorized_pickup && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-0.5">Authorized Pickup</p>
                    <p className="text-sm text-amber-900 whitespace-pre-wrap">{camper.authorized_pickup}</p>
                  </div>
                )}
                {camper.travel_additional_info && (
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-0.5">Travel / Additional Info</p>
                    <p className="text-sm text-slate-900 whitespace-pre-wrap">{camper.travel_additional_info}</p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Flex Schedule for flex campers */}
          {camper.is_flex && camper.flex_days && camper.flex_days.length > 0 && (
            <section data-testid="flex-schedule-section">
              <h3 className="font-heading font-semibold text-base sm:text-lg flex items-center gap-2 mb-3 text-primary-700">
                <CalendarDays className="w-4 h-4 sm:w-5 sm:h-5" />
                Flex Schedule ({camper.flex_days.length} days)
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {camper.flex_days.map(day => {
                  const d = new Date(day + "T12:00:00");
                  const label = d.toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'short', month: 'short', day: 'numeric' });
                  const isToday = day === new Date().toISOString().split('T')[0];
                  const isTripDay = (camper.cit_trip_dates || []).includes(day);
                  const isBroadway = camper.broadway_trip && day === "2026-07-22";
                  const isOvernight = camper.overnight_trip && ["2026-07-28", "2026-07-29", "2026-07-30"].includes(day);
                  const isWaitlist = camper.overnight_waitlist && !camper.overnight_trip && ["2026-07-28", "2026-07-29", "2026-07-30"].includes(day);
                  const isGreen = isTripDay || isBroadway || isOvernight;
                  const tripLabel = isBroadway ? " (Broadway)" : isOvernight ? " (Overnight)" : isWaitlist ? " (Waitlist)" : isTripDay ? " (Trip)" : "";
                  return (
                    <Badge 
                      key={day} 
                      variant={isGreen || isWaitlist || isToday ? "default" : "secondary"}
                      className={`text-xs ${
                        isWaitlist ? 'bg-purple-600 hover:bg-purple-700 text-white' :
                        isGreen ? 'bg-green-600 hover:bg-green-700 text-white' :
                        isToday ? 'bg-blue-600' : ''
                      }`}
                      data-testid={`flex-day-${day}`}
                    >
                      {label}{tripLabel}
                    </Badge>
                  );
                })}
              </div>
            </section>
          )}

          {/* Medical Information - organized by section */}
          {(() => {
            const sectionOrder = ["Medical", "Family", "Behavioral", "Additional Info", "Skippers Only", "Transportation", "Swim"];
            const sectionIcons = {
              "Medical": Stethoscope,
              "Family": Heart,
              "Behavioral": Brain,
              "Additional Info": Star,
              "Skippers Only": FileText,
              "Transportation": Bus,
              "Swim": Waves,
            };
            const sectionColors = {
              "Medical": "text-red-700",
              "Family": "text-blue-700",
              "Behavioral": "text-purple-700",
              "Additional Info": "text-green-700",
              "Skippers Only": "text-orange-700",
              "Transportation": "text-teal-700",
              "Swim": "text-cyan-700",
            };
            const hasSections = typeof Object.values(medicalData)[0] === 'object';
            
            if (hasSections) {
              return sectionOrder.map(section => {
                let data = medicalData[section];
                // Read swim color DIRECTLY from camper.swim_color (the same
                // field the daily roster uses) so this card can never get
                // out of sync.
                if (section === "Swim") {
                  data = { ...(data || {}) };
                  if (camper.swim_color) data["Swim Color"] = camper.swim_color;
                  if (camper.swim_level) data["Swim Level"] = camper.swim_level;
                }
                if (!data || Object.keys(data).length === 0) return null;
                
                // Skip "Skippers Only" if not in N2-N16 groups
                if (section === "Skippers Only") {
                  const code = (camper.group_code || "").toUpperCase();
                  const num = parseInt(code.replace(/\D/g, ''), 10);
                  if (!code.startsWith('N') || num < 2 || num > 16) return null;
                }
                
                const Icon = sectionIcons[section] || FileText;
                const color = sectionColors[section] || "text-primary-700";
                
                return (
                  <section key={section}>
                    <h3 className={`font-heading font-semibold text-base sm:text-lg flex items-center gap-2 mb-3 ${color}`}>
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                      {section}
                    </h3>
                    <div className="grid gap-2 sm:gap-3">
                      {Object.entries(data)
                        .sort((a, b) => {
                          if (section === "Transportation") {
                            const order = ["Bus# AM", "Trans-AM Drop Off Method", "Bus# PM", "Trans-PM Dismissal Method"];
                            return (order.indexOf(a[0]) === -1 ? 99 : order.indexOf(a[0])) - (order.indexOf(b[0]) === -1 ? 99 : order.indexOf(b[0]));
                          }
                          return 0;
                        })
                        .map(([key, value]) => {
                        const isAlert = key.toLowerCase().includes('allerg') || 
                                        key.toLowerCase().includes('meds') ||
                                        key.toLowerCase().includes('reaction');
                        return (
                          <div key={key} className={`p-3 sm:p-4 rounded-lg border ${
                            isAlert && section === "Medical" ? 'bg-red-50 border-red-200' : 'bg-muted/30'
                          }`} data-testid={`field-${key.replace(/[\s/]+/g, '-').toLowerCase()}`}>
                            <p className={`text-xs font-medium uppercase tracking-wider mb-1 ${
                              isAlert && section === "Medical" ? 'text-red-600' : 'text-muted-foreground'
                            }`}>
                              {isAlert && section === "Medical" && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                              {key}
                            </p>
                            <p className={`text-sm ${isAlert && section === "Medical" ? 'text-red-800 font-medium' : ''}`}>
                              {value || "—"}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                );
              });
            } else if (Object.keys(medicalData).length > 0) {
              // Flat format (old data)
              return (
                <section>
                  <h3 className="font-heading font-semibold text-base sm:text-lg flex items-center gap-2 mb-3 text-primary-700">
                    <Stethoscope className="w-4 h-4 sm:w-5 sm:h-5" />
                    Medical Information
                  </h3>
                  <div className="grid gap-2 sm:gap-3">
                    {Object.entries(medicalData).map(([key, value]) => (
                      <div key={key} className="p-3 rounded-lg border bg-muted/30">
                        <p className="text-xs font-medium uppercase tracking-wider mb-1 text-muted-foreground">{key}</p>
                        <p className="text-sm">{value || "—"}</p>
                      </div>
                    ))}
                  </div>
                </section>
              );
            } else {
              return (
                <section>
                  <h3 className="font-heading font-semibold text-base sm:text-lg flex items-center gap-2 mb-3 text-primary-700">
                    <Stethoscope className="w-4 h-4 sm:w-5 sm:h-5" />
                    Medical Information
                  </h3>
                  <div className="text-center py-6 bg-muted/30 rounded-lg">
                    <Stethoscope className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No medical data on file</p>
                  </div>
                </section>
              );
            }
          })()}

          {/* Camper Notes — admin / supervisor only. Regular counselors never see this section.
              CIT counselors (user group_code starts with "CIT") CAN see/edit notes for CIT campers. */}
          {(() => {
            const userIsCIT = (userGroupCode || "").toUpperCase().startsWith("CIT");
            const camperIsCIT = (camper.group_code || "").toUpperCase().startsWith("CIT");
            if (userIsCIT && camperIsCIT) {
              return <CitCamperNotesEditor camperId={camper.id} readOnly={readOnly} />;
            }
            return null;
          })()}

          {/* Health / Nurse Notes */}
          <HealthNotesDisplay camperId={camper.id} />

          {/* Medical Information - organized by section */}
          {/* Additional Info */}
          <section className="text-xs text-muted-foreground border-t pt-4">
            <p>Person ID: {camper.person_id}</p>
            {camper.date_of_birth && <p>DOB: {camper.date_of_birth}</p>}
            {camper.last_synced && <p>Last Synced: {new Date(camper.last_synced).toLocaleString("en-US", { timeZone: "America/New_York" })}</p>}
          </section>
        </div>
      </ScrollArea>
    </>
  );
}

function IncidentNotes2025Section({ camperId, initialNotes, readOnly }) {
  const [allNotes, setAllNotes] = useState(initialNotes || "");
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);

  const handleAddNote = async () => {
    if (!newNote.trim() || readOnly) return;
    const dateStr = new Date().toLocaleDateString("en-US", { timeZone: "America/New_York", month: "2-digit", day: "2-digit", year: "numeric" });
    const entry = `[${dateStr}] ${newNote.trim()}`;
    const updated = allNotes ? `${allNotes}\n${entry}` : entry;
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/campers/${camperId}/incident-notes-2025`, { notes: updated }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllNotes(updated);
      setNewNote("");
      toast.success("Note added");
    } catch {
      toast.error("Failed to save note");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (lineIdx) => {
    if (readOnly) return;
    const lines = allNotes.split("\n").filter((_, i) => i !== lineIdx);
    const updated = lines.join("\n");
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/campers/${camperId}/incident-notes-2025`, { notes: updated }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllNotes(updated);
      toast.success("Note deleted");
    } catch {
      toast.error("Failed to delete note");
    }
  };

  return (
    <section data-testid="incident-notes-2025-section">
      <h3 className="font-heading font-semibold text-base flex items-center gap-2 mb-2 text-purple-700">
        <FileText className="w-4 h-4" />Camper Notes
        {readOnly && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50 ml-1">Read Only</Badge>}
      </h3>
      {allNotes && (
        <div className="mb-2 p-2 rounded-lg border bg-purple-50/40 max-h-[160px] overflow-y-auto" data-testid="camper-notes-log">
          {allNotes.split("\n").filter(l => l.trim()).map((line, i) => (
            <div key={i} className="flex items-start gap-1 group py-0.5">
              <p className="text-sm flex-1">{line}</p>
              {!readOnly && (
                <button onClick={() => handleDeleteNote(i)} className="shrink-0 text-red-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity p-0.5" data-testid={`delete-note-${i}`} title="Delete note">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {!readOnly && (
        <div className="space-y-2">
          <textarea
            className="w-full min-h-[44px] max-h-[100px] p-2.5 text-sm border rounded-lg bg-white focus:ring-2 focus:ring-purple-300 focus:border-purple-400 outline-none resize-y"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Type a new note... (date added automatically)"
            data-testid="incident-notes-2025-textarea"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAddNote(); } }}
          />
          <Button size="sm" onClick={handleAddNote} disabled={saving || !newNote.trim()} className="w-full bg-purple-600 hover:bg-purple-700 text-xs h-8" data-testid="incident-notes-2025-save">
            <Save className="w-3 h-3 mr-1" />{saving ? "Saving..." : "Add Note"}
          </Button>
        </div>
      )}
      {!readOnly && <p className="text-[10px] text-muted-foreground mt-1">Press Enter to add. Date is stamped automatically.</p>}
    </section>
  );
}


function CitCamperNotesEditor({ camperId, readOnly }) {
  const [notes, setNotes] = useState("");
  const [draft, setDraft] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [legacyNotes, setLegacyNotes] = useState([]);
  const [legacyOpen, setLegacyOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/campers/${camperId}/notes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!alive) return;
        setNotes(res.data?.notes || "");
        setDraft(res.data?.notes || "");
        setUpdatedAt(res.data?.updated_at || null);
        setUpdatedBy(res.data?.updated_by || null);
        setLegacyNotes(Array.isArray(res.data?.legacy_notes) ? res.data.legacy_notes : []);
      } catch {
        if (alive) toast.error("Failed to load notes");
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [camperId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/campers/${camperId}/notes`, { notes: draft }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotes(res.data?.notes || "");
      setUpdatedAt(res.data?.updated_at || null);
      setUpdatedBy(res.data?.updated_by || null);
      toast.success("Notes saved");
    } catch {
      toast.error("Failed to save notes");
    } finally {
      setSaving(false);
    }
  };

  const dirty = draft !== notes;
  const updatedDisplay = updatedAt
    ? new Date(updatedAt).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
    : null;

  return (
    <section data-testid="cit-camper-notes-section">
      <h3 className="font-heading font-semibold text-base flex items-center gap-2 mb-2 text-purple-700">
        <FileText className="w-4 h-4" />Notes
        <Badge variant="outline" className="text-[10px] border-purple-300 text-purple-700 bg-purple-50 ml-1">CIT Supervisors & Admin Only</Badge>
        {readOnly && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50 ml-1">Read Only</Badge>}
      </h3>
      {loading ? (
        <p className="text-xs text-muted-foreground italic">Loading notes…</p>
      ) : (
        <div className="space-y-2">
          <textarea
            className="w-full min-h-[110px] p-2.5 text-sm border rounded-lg bg-white focus:ring-2 focus:ring-purple-300 focus:border-purple-400 outline-none resize-y"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Private notes about this CIT. Visible only to CIT supervisors (CIT login) and Admin."
            data-testid="cit-camper-notes-textarea"
            readOnly={readOnly}
          />
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] text-muted-foreground">
              {updatedDisplay
                ? <>Last edited{updatedBy ? <> by <span className="font-semibold">{updatedBy}</span></> : ""} · {updatedDisplay}</>
                : "Not edited yet."}
            </p>
            {!readOnly && (
              <Button size="sm" onClick={handleSave} disabled={saving || !dirty} className="bg-purple-600 hover:bg-purple-700 text-xs h-8" data-testid="cit-camper-notes-save">
                <Save className="w-3 h-3 mr-1" />{saving ? "Saving…" : "Save Notes"}
              </Button>
            )}
          </div>
          {legacyNotes.length > 0 && (
            <div className="mt-3 border-t pt-3" data-testid="legacy-notes-block">
              <button
                type="button"
                onClick={() => setLegacyOpen(v => !v)}
                className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900"
                data-testid="legacy-notes-toggle"
              >
                <FileText className="w-3.5 h-3.5" />
                Legacy Notes (CampMinder &amp; prior seasons)
                <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[10px] bg-slate-200 text-slate-700">{legacyNotes.length}</Badge>
                {legacyOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
              {legacyOpen && (
                <div className="mt-2 space-y-2 max-h-[260px] overflow-y-auto pr-1">
                  {legacyNotes.map((ln, i) => (
                    <div key={i} className="p-2 rounded-md border border-slate-200 bg-slate-50/60" data-testid={`legacy-note-${i}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-700 bg-white">
                          {ln.year ? ln.year : "Undated"}
                        </Badge>
                        <span className="text-[10px] text-slate-500">{ln.source}</span>
                      </div>
                      <p className="text-xs text-slate-700 whitespace-pre-wrap break-words">{ln.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}



function HealthNotesDisplay({ camperId }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!camperId) return;
    const fetchNotes = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/health/notes/${camperId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setNotes(res.data);
      } catch { /* ignore - might not have permission */ }
      finally { setLoading(false); }
    };
    fetchNotes();
  }, [camperId, token]);

  if (loading || notes.length === 0) return null;

  return (
    <section data-testid="health-notes-section">
      <h3 className="font-heading font-semibold text-base flex items-center gap-2 mb-2 text-teal-700">
        <Stethoscope className="w-4 h-4" />
        Nurse Notes ({notes.length})
      </h3>
      <div className="space-y-2">
        {notes.map((n, i) => (
          <div key={i} className="bg-teal-50 rounded-lg px-3 py-2 border border-teal-100" data-testid={`counselor-health-note-${i}`}>
            <p className="text-sm">{n.note}</p>
            <p className="text-[10px] text-muted-foreground mt-1">
              {new Date(n.timestamp).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              {" — "}{n.added_by}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}


function AttendanceView({ user, campers: allCampers, attendanceData, setAttendanceData, attendanceConflicts, setAttendanceConflicts, attendanceSaving, setAttendanceSaving, onBack }) {
  const [loading, setLoading] = useState(true);
  const [localData, setLocalData] = useState({});
  const [resolvedConflicts, setResolvedConflicts] = useState({});
  const [selectedDate, setSelectedDate] = useState("");
  const [campDays, setCampDays] = useState([]);
  const [dayCampers, setDayCampers] = useState([]);
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  // Load camp calendar on mount
  useEffect(() => {
    (async () => {
      try {
        const calRes = await axios.get(`${API}/attendance/calendar`);
        const days = calRes.data.camp_days || [];
        setCampDays(days);
        // Default to today if it's a camp day, otherwise first upcoming
        const todayStr = new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
        const defaultDate = days.includes(todayStr) ? todayStr : days.find(d => d >= todayStr) || days[0] || "";
        setSelectedDate(defaultDate);
      } catch {
        toast.error("Failed to load calendar");
      }
    })();
  }, []);

  // Load roster + attendance when date changes
  useEffect(() => {
    if (!selectedDate) return;
    const loadData = async () => {
      setLoading(true);
      try {
        const [rosterRes, attRes] = await Promise.all([
          axios.get(`${API}/roster/today`, { headers, params: { date: selectedDate } }),
          axios.get(`${API}/attendance/status`, { headers, params: { date: selectedDate } }),
        ]);
        setDayCampers(rosterRes.data.campers || []);
        setLocalData(attRes.data.attendance || {});
        setAttendanceConflicts(attRes.data.conflicts || {});
        setResolvedConflicts(attRes.data.resolved_conflicts || {});
      } catch {
        toast.error("Failed to load roster");
      }
      setLoading(false);
    };
    loadData();
    // Auto-refresh every 10s to pick up pushes from Attendance App and Bus App
    // Conflict rows are locked — poll updates attendance but preserves existing conflicts
    const poll = setInterval(async () => {
      try {
        const attRes = await axios.get(`${API}/attendance/status`, { headers, params: { date: selectedDate } });
        const newAtt = attRes.data.attendance || {};
        const newConflicts = attRes.data.conflicts || {};
        const newResolved = attRes.data.resolved_conflicts || {};
        // Update attendance data freely (so new marks appear)
        setLocalData(newAtt);
        // For conflicts: only ADD new conflicts, never remove existing ones (only resolve action removes)
        setAttendanceConflicts(prev => {
          const merged = { ...prev };
          Object.keys(newConflicts).forEach(cid => { merged[cid] = newConflicts[cid]; });
          return merged;
        });
        setResolvedConflicts(newResolved);
      } catch { /* silent */ }
    }, 10000);
    return () => clearInterval(poll);
  }, [selectedDate]);

  const dateLabel = selectedDate ? new Date(selectedDate + "T12:00:00").toLocaleDateString("en-US", { timeZone: "America/New_York", weekday: "long", month: "long", day: "numeric", year: "numeric" }) : "";

  const toggleCamper = async (camperId, currentStatus) => {
    const newStatus = currentStatus === "present" ? "absent" : "present";
    setLocalData(prev => ({ ...prev, [camperId]: newStatus }));
    try {
      await axios.post(`${API}/attendance/mark`, { camper_id: camperId, status: newStatus, date: selectedDate }, { headers });
      // Immediately refresh to pick up any new conflicts
      const attRes = await axios.get(`${API}/attendance/status`, { headers, params: { date: selectedDate } });
      setLocalData(attRes.data.attendance || {});
      setAttendanceConflicts(attRes.data.conflicts || {});
      setResolvedConflicts(attRes.data.resolved_conflicts || {});
    } catch {
      toast.error("Failed to save");
      setLocalData(prev => ({ ...prev, [camperId]: currentStatus }));
    }
  };

  const markAll = async (status) => {
    setAttendanceSaving(true);
    const records = dayCampers.map(c => ({ camper_id: c.id, status }));
    const newData = {};
    dayCampers.forEach(c => { newData[c.id] = status; });
    setLocalData(newData);
    try {
      await axios.post(`${API}/attendance/bulk`, { records, date: selectedDate }, { headers });
      // Immediately refresh to pick up any new conflicts
      const attRes = await axios.get(`${API}/attendance/status`, { headers, params: { date: selectedDate } });
      setLocalData(attRes.data.attendance || {});
      setAttendanceConflicts(attRes.data.conflicts || {});
      setResolvedConflicts(attRes.data.resolved_conflicts || {});
      toast.success(`All marked ${status}`);
    } catch {
      toast.error("Failed to save");
    }
    setAttendanceSaving(false);
  };

  const resolveConflict = async (camperId, accept) => {
    try {
      await axios.post(`${API}/attendance/resolve-conflict`, { camper_id: camperId, date: selectedDate, accept }, { headers });
      // Immediately refresh
      const attRes = await axios.get(`${API}/attendance/status`, { headers, params: { date: selectedDate } });
      setLocalData(attRes.data.attendance || {});
      setAttendanceConflicts(attRes.data.conflicts || {});
      setResolvedConflicts(attRes.data.resolved_conflicts || {});
      toast.success(`Conflict resolved — accepted ${accept === "app" ? "Attendance App" : "Bus App"} mark`);
    } catch {
      toast.error("Failed to resolve conflict");
    }
  };

  const printAttendance = () => {
    const groupName = user?.group_name || user?.group_code || "Group";
    const resolvedCount = Object.keys(resolvedConflicts).length;

    // Build camper rows
    const camperRows = dayCampers.map(c => {
      const s = localData[c.id] || "unmarked";
      const conflict = attendanceConflicts[c.id];
      const resolved = resolvedConflicts[c.id];
      const name = c.first_name + " " + c.last_name;
      const statusLabel = conflict ? "CONFLICT" : s.charAt(0).toUpperCase() + s.slice(1);
      let note = "";
      let rowClass = conflict ? "conflict" : s;
      if (conflict) {
        note = '<span class="conflict-note">Attendance App: ' + conflict.app_status + ' | Bus App: ' + conflict.bus_status + '</span>';
      } else if (resolved) {
        rowClass = "resolved";
        const src = resolved.accepted_source === "app" ? "Attendance App" : "Bus App";
        note = '<span class="resolved-note">Conflict resolved — accepted ' + src + ' mark (' + resolved.resolved_status + ') by ' + (resolved.resolved_by || "unknown") + '</span>';
      }
      return '<tr class="row-' + rowClass + '"><td>' + name + '</td><td>' + statusLabel + '</td><td>' + note + '</td></tr>';
    }).join("");

    // Build resolution log rows
    let resolutionLog = "";
    if (resolvedCount > 0) {
      const logRows = dayCampers.filter(c => resolvedConflicts[c.id]).map(c => {
        const r = resolvedConflicts[c.id];
        const name = c.first_name + " " + c.last_name;
        const src = r.accepted_source === "app" ? "Accepted Attendance App" : "Accepted Bus App";
        const time = r.resolved_at ? new Date(r.resolved_at).toLocaleString("en-US", {timeZone: "America/New_York", hour: "numeric", minute: "2-digit", hour12: true}) : "—";
        return '<tr><td>' + name + '</td><td>' + src + ' (' + r.resolved_status + ')</td><td>' + (r.resolved_by || "—") + '</td><td>' + time + '</td></tr>';
      }).join("");
      resolutionLog = '<h2>Conflict Resolution Log</h2><table><thead><tr><th>Camper</th><th>Resolution</th><th>Resolved By</th><th>Time</th></tr></thead><tbody>' + logRows + '</tbody></table>';
    }

    const printContent = '<!DOCTYPE html><html><head><title>Attendance Report - ' + groupName + ' - ' + dateLabel + '</title>' +
      '<style>' +
      'body { font-family: Arial, sans-serif; padding: 20px; color: #1a1a1a; }' +
      'h1 { font-size: 18px; margin-bottom: 4px; }' +
      '.subtitle { font-size: 13px; color: #555; margin-bottom: 16px; }' +
      '.stats { display: flex; gap: 16px; margin-bottom: 16px; font-size: 13px; flex-wrap: wrap; }' +
      '.stat { padding: 4px 10px; border-radius: 4px; font-weight: bold; }' +
      '.present { background: #d1fae5; color: #065f46; }' +
      '.absent { background: #fee2e2; color: #991b1b; }' +
      '.late { background: #fef3c7; color: #92400e; }' +
      '.unmarked { background: #f3f4f6; color: #374151; }' +
      '.conflict { background: #fecaca; color: #991b1b; }' +
      '.resolved { background: #dbeafe; color: #1e40af; }' +
      'table { width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 20px; }' +
      'th { text-align: left; padding: 8px; border-bottom: 2px solid #ddd; font-weight: bold; }' +
      'td { padding: 6px 8px; border-bottom: 1px solid #eee; }' +
      'tr.row-present td:first-child { border-left: 3px solid #10b981; }' +
      'tr.row-absent td:first-child { border-left: 3px solid #ef4444; }' +
      'tr.row-late td:first-child { border-left: 3px solid #f59e0b; }' +
      'tr.row-conflict td:first-child { border-left: 3px solid #ef4444; }' +
      'tr.row-conflict { background: #fff1f2; }' +
      'tr.row-resolved td:first-child { border-left: 3px solid #3b82f6; }' +
      'tr.row-resolved { background: #eff6ff; }' +
      '.conflict-note { font-size: 11px; color: #991b1b; font-style: italic; }' +
      '.resolved-note { font-size: 11px; color: #1e40af; font-style: italic; }' +
      'h2 { font-size: 15px; margin-top: 24px; margin-bottom: 8px; border-bottom: 1px solid #ddd; padding-bottom: 4px; }' +
      '@media print { body { padding: 10px; } }' +
      '</style></head><body>' +
      '<h1>' + groupName + ' — Attendance Report</h1>' +
      '<div class="subtitle">' + dateLabel + '</div>' +
      '<div class="stats">' +
      '<span class="stat present">' + presentCount + ' Present</span>' +
      '<span class="stat absent">' + absentCount + ' Absent</span>' +
      (lateCount > 0 ? '<span class="stat late">' + lateCount + ' Late</span>' : '') +
      '<span class="stat unmarked">' + unmarkedCount + ' Unmarked</span>' +
      (conflictCount > 0 ? '<span class="stat conflict">' + conflictCount + ' Active Conflict' + (conflictCount > 1 ? 's' : '') + '</span>' : '') +
      (resolvedCount > 0 ? '<span class="stat resolved">' + resolvedCount + ' Resolved</span>' : '') +
      '</div>' +
      '<table><thead><tr><th>Camper</th><th>Status</th><th>Notes</th></tr></thead><tbody>' +
      camperRows +
      '</tbody></table>' +
      resolutionLog +
      '</body></html>';

    const printWin = window.open("", "_blank");
    printWin.document.write(printContent);
    printWin.document.close();
    printWin.focus();
    printWin.print();
  };

  const presentCount = dayCampers.filter(c => localData[c.id] === "present").length;
  const absentCount = dayCampers.filter(c => localData[c.id] === "absent").length;
  const lateCount = dayCampers.filter(c => localData[c.id] === "late").length;
  const unmarkedCount = dayCampers.length - presentCount - absentCount - lateCount;
  const conflictCount = Object.keys(attendanceConflicts).length;

  const goDay = (dir) => {
    const idx = campDays.indexOf(selectedDate);
    const next = idx + dir;
    if (next >= 0 && next < campDays.length) setSelectedDate(campDays[next]);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-2xl mx-auto px-3 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onBack} className="px-2" data-testid="attendance-back">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-sm font-bold text-emerald-800">Daily Attendance</h1>
              <p className="text-[10px] text-muted-foreground">{dayCampers.length} campers scheduled</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{presentCount} Present</Badge>
            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">{absentCount} Absent</Badge>
            {lateCount > 0 && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">{lateCount} Late</Badge>}
            {unmarkedCount > 0 && <Badge variant="outline">{unmarkedCount} Unmarked</Badge>}
            {conflictCount > 0 && <Badge variant="outline" className="bg-red-100 text-red-800 border-red-400">{conflictCount} Conflict{conflictCount > 1 ? "s" : ""}</Badge>}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-3 sm:p-4 space-y-4">
        {/* Date picker */}
        <div className="flex items-center gap-2 bg-emerald-50 rounded-xl border border-emerald-200 p-2">
          <Button variant="ghost" size="sm" className="px-2" onClick={() => goDay(-1)} disabled={campDays.indexOf(selectedDate) <= 0} data-testid="attendance-prev-day">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1 text-center">
            <input
              type="date"
              value={selectedDate}
              onChange={e => { if (campDays.includes(e.target.value)) setSelectedDate(e.target.value); else toast.error("Not a camp day"); }}
              className="bg-transparent text-center text-sm font-semibold text-emerald-800 border-0 outline-none cursor-pointer"
              data-testid="attendance-date-picker"
            />
            <p className="text-[10px] text-emerald-600">{dateLabel}</p>
          </div>
          <Button variant="ghost" size="sm" className="px-2" onClick={() => goDay(1)} disabled={campDays.indexOf(selectedDate) >= campDays.length - 1} data-testid="attendance-next-day">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Quick actions — counselors can print; All Present / All Absent removed (attendance is done in the external Group Attendance app) */}
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50 px-3" onClick={() => printAttendance()} data-testid="attendance-print">
            <Printer className="w-4 h-4" />
          </Button>
        </div>

        {/* Conflict Banner */}
        {conflictCount > 0 && (
          <div className="p-4 rounded-xl border-2 border-red-600 bg-red-600 shadow-lg animate-pulse" data-testid="attendance-conflict-alert">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-white flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-base font-black text-white uppercase tracking-wide">STOP — DO NOT RESOLVE CONFLICTS YOURSELF</p>
                <p className="text-sm text-red-100 mt-1 font-semibold">{conflictCount} camper(s) have a mismatch between the Attendance App and Bus App. You MUST notify your Supervisor BEFORE selecting any resolution. Do NOT tap Accept App or Accept Bus until your Supervisor tells you which one to accept.</p>
              </div>
            </div>
          </div>
        )}

        {/* Camper list */}
        {loading ? (
          <div className="text-center py-10 text-muted-foreground">Loading roster...</div>
        ) : dayCampers.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">No campers scheduled for this day</div>
        ) : (
          <div className="space-y-1.5">
            {dayCampers.map(c => {
              const status = localData[c.id] || "unmarked";
              const display = c.first_name;
              const conflict = attendanceConflicts[c.id];
              const resolved = resolvedConflicts[c.id];
              return (
                <div key={c.id}>
                  <button
                    onClick={() => toggleCamper(c.id, status)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 active:scale-[0.98] ${
                      conflict ? "border-red-500 bg-red-50 shadow-md" :
                      resolved ? "border-blue-300 bg-blue-50" :
                      status === "present" ? "bg-emerald-50 border-emerald-300" :
                      status === "absent" ? "bg-red-50 border-red-300" :
                      status === "late" ? "bg-amber-50 border-amber-300" :
                      "bg-white border-gray-200 hover:border-gray-300"
                    }`}
                    data-testid={`attendance-camper-${c.id}`}
                  >
                    <div className="flex-shrink-0">
                      {conflict ? (
                        <AlertTriangle className="w-6 h-6 text-red-600" />
                      ) : status === "present" ? (
                        <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                      ) : status === "absent" ? (
                        <XCircle className="w-6 h-6 text-red-500" />
                      ) : status === "late" ? (
                        <Clock className="w-6 h-6 text-amber-600" />
                      ) : (
                        <Circle className="w-6 h-6 text-gray-300" />
                      )}
                    </div>
                    <span className={`flex-1 text-left text-sm font-medium ${conflict ? "text-red-900" : status === "absent" ? "text-red-700 line-through" : ""}`}>
                      {display} {c.last_name}
                    </span>
                    {conflict ? (
                      <Badge variant="outline" className="text-[10px] bg-red-200 text-red-900 border-red-500 font-bold">
                        CONFLICT
                      </Badge>
                    ) : resolved ? (
                      <Badge variant="outline" className="text-[10px] bg-blue-100 text-blue-800 border-blue-300 font-medium">
                        Resolved
                      </Badge>
                    ) : (
                      <Badge variant="outline" className={`text-[10px] ${
                        status === "present" ? "bg-emerald-100 text-emerald-700 border-emerald-200" :
                        status === "absent" ? "bg-red-100 text-red-700 border-red-200" :
                        status === "late" ? "bg-amber-100 text-amber-700 border-amber-200" :
                        "text-muted-foreground"
                      }`}>
                        {status === "present" ? "Present" : status === "absent" ? "Absent" : status === "late" ? "Late" : "Tap to mark"}
                      </Badge>
                    )}
                  </button>
                  {conflict && (
                    <div className="ml-9 mt-1 mb-1 space-y-1.5" data-testid={`conflict-detail-${c.id}`}>
                      <div className="px-3 py-2 rounded-lg bg-red-600 text-white text-[11px] font-black" data-testid={`conflict-warning-${c.id}`}>
                        <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                        NOTIFY YOUR SUPERVISOR BEFORE RESOLVING THIS CONFLICT
                      </div>
                      <div className="px-3 py-2 rounded-lg bg-red-100 border border-red-300 text-[11px] text-red-900">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span><span className="font-bold">Attendance App:</span> {conflict.app_status} &nbsp;|&nbsp; <span className="font-bold">Bus App:</span> {conflict.bus_status}</span>
                          <div className="flex gap-1.5">
                            <button
                              onClick={(e) => { e.stopPropagation(); resolveConflict(c.id, "app"); }}
                              className="px-2 py-0.5 rounded bg-blue-600 text-white text-[10px] font-bold hover:bg-blue-700 active:scale-95 transition-all"
                              data-testid={`resolve-accept-app-${c.id}`}
                            >
                              Accept App
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); resolveConflict(c.id, "bus"); }}
                              className="px-2 py-0.5 rounded bg-gray-700 text-white text-[10px] font-bold hover:bg-gray-800 active:scale-95 transition-all"
                              data-testid={`resolve-accept-bus-${c.id}`}
                            >
                              Accept Bus
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {resolved && !conflict && (
                    <div className="ml-9 mt-1 mb-1 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-[11px] text-blue-800" data-testid={`resolved-detail-${c.id}`}>
                      Conflict resolved — accepted {resolved.accepted_source === "app" ? "Attendance App" : "Bus App"} mark ({resolved.resolved_status}) by {resolved.resolved_by || "unknown"}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
